import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { TrendingUp, Users, User, Package, LogOut, Mail, Server, Plus, Edit, Trash2, Eye, Loader2, Check, AlertCircle, Globe, Search, RotateCcw, Bot, Key, Shield, Brain, RefreshCw, ChevronDown, ChevronUp, Sliders, MessageSquare, History, Clock, Settings, X, Wallet, Target, ThumbsUp, ThumbsDown, PlayCircle, Send, HelpCircle, Building, BarChart3, Calculator, Boxes, Receipt, LayoutDashboard, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";
import { format, parseISO } from "date-fns";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { brandName, refreshBrand } = useBrand();
  const { refreshTranslations } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [smtpConfig, setSmtpConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // SMTP form state
  const [smtpForm, setSmtpForm] = useState({ host: "", port: "587", use_tls: true, username: "", password: "", from_email: "", from_name: "", brand_name: "" });
  const [isSavingSmtp, setIsSavingSmtp] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [isTesting, setIsTesting] = useState(false);
  
  // Package dialog state
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [packageForm, setPackageForm] = useState({ 
    name: "", 
    description: "", 
    monthly_limit: "", 
    sort_order: 0,
    ai_enabled: false,
    ai_token_limit: 0,
    tavily_token_limit: 0,
    perplexity_token_limit: 0
  });
  const [isSavingPackage, setIsSavingPackage] = useState(false);
  
  // User overview dialog state
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userOverview, setUserOverview] = useState(null);
  const [isLoadingOverview, setIsLoadingOverview] = useState(false);
  
  // Languages state
  const [languages, setLanguages] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [selectedLangForEdit, setSelectedLangForEdit] = useState(null);
  const [selectedNamespace, setSelectedNamespace] = useState("all");
  const [translationSearch, setTranslationSearch] = useState("");
  const [editedTranslations, setEditedTranslations] = useState({});
  const [isSavingTranslations, setIsSavingTranslations] = useState(false);
  const [isLoadingTranslations, setIsLoadingTranslations] = useState(false);

  // AI Configuration state
  const [aiConfig, setAiConfig] = useState(null);
  const [aiModels, setAiModels] = useState([]);
  const [isLoadingAiModels, setIsLoadingAiModels] = useState(false);
  const [modelSearchQuery, setModelSearchQuery] = useState("");
  const [aiConfigForm, setAiConfigForm] = useState({
    openrouter_api_key: "",
    openrouter_provisioning_key: "",
    tavily_api_key: "",
    perplexity_api_key: "",
    default_model_id: "",
    fallback_model_ids: [],
    pdf_processing_model_id: "",
    // Legacy fields (for backward compatibility)
    default_system_prompt: "",
    custom_system_prompt: "",
    platform_help_guide: "",
    platform_help_enabled: true,
    // Individual (Personal) User AI Settings
    individual_default_system_prompt: "",
    individual_custom_system_prompt: "",
    individual_platform_help_guide: "",
    individual_platform_help_enabled: true,
    // Business (Company) User AI Settings
    business_default_system_prompt: "",
    business_custom_system_prompt: "",
    business_platform_help_guide: "",
    business_platform_help_enabled: true,
    enabled_ai_languages: ["en"],
    use_platform_translations: true
  });
  const [isSavingAiConfig, setIsSavingAiConfig] = useState(false);
  const [isSavingDefaultPrompt, setIsSavingDefaultPrompt] = useState(false);
  const [isSavingHelpGuide, setIsSavingHelpGuide] = useState(false);
  const [isSavingLanguageConfig, setIsSavingLanguageConfig] = useState(false);
  // Factory defaults for each account type
  const [factoryDefaultPrompt, setFactoryDefaultPrompt] = useState("");
  const [defaultHelpGuide, setDefaultHelpGuide] = useState("");
  const [individualFactoryDefaultPrompt, setIndividualFactoryDefaultPrompt] = useState("");
  const [businessFactoryDefaultPrompt, setBusinessFactoryDefaultPrompt] = useState("");
  const [individualDefaultHelpGuide, setIndividualDefaultHelpGuide] = useState("");
  const [businessDefaultHelpGuide, setBusinessDefaultHelpGuide] = useState("");
  const [availableLanguages, setAvailableLanguages] = useState([]);
  // Account type tab for AI configuration
  const [aiAccountTypeTab, setAiAccountTypeTab] = useState("individual");
  const [showApiKeys, setShowApiKeys] = useState({ openrouter: false, provisioning: false, tavily: false, perplexity: false });
  
  // Thinking Layer state
  const [thinkingConfig, setThinkingConfig] = useState(null);
  const [thinkingForm, setThinkingForm] = useState({
    intent_classification: { model_id: "", prompt_template: "", show_to_user: true },
    source_ranking: { model_id: "", user_data_weight: 70, admin_kb_weight: 60, web_search_weight: 40, max_sources: 8, show_to_user: true },
    response_generation: { style: "professional_friendly", max_tokens: 2000, show_sources: true, temperature: 0.7 },
    web_search: { enabled: false, provider: "tavily", auto_search: false, max_results: 3 }
  });
  const [isSavingThinking, setIsSavingThinking] = useState(false);
  
  // Guardrails state
  const [guardrailsConfig, setGuardrailsConfig] = useState(null);
  const [guardrailsForm, setGuardrailsForm] = useState({
    prevent_off_topic: true,
    off_topic_message: "",
    block_harmful_content: true,
    harmful_content_message: "",
    max_response_length: 2000,
    disable_response_length_limit: false,
    length_exceeded_message: "",
    restrict_sensitive_data: true,
    sensitive_data_message: "",
    custom_restrictions: []
  });
  const [isSavingGuardrails, setIsSavingGuardrails] = useState(false);
  const [newRestriction, setNewRestriction] = useState({ pattern: "", message: "" });

  // Company Pages Visibility state (Admin-controlled)
  const [disabledCompanyPages, setDisabledCompanyPages] = useState([]);
  const [isLoadingCompanyPages, setIsLoadingCompanyPages] = useState(false);

  // Individual Pages Visibility state (Admin-controlled)
  const [disabledIndividualPages, setDisabledIndividualPages] = useState([]);

  // Knowledge Base state
  const [kbDocuments, setKbDocuments] = useState([]);
  const [kbStats, setKbStats] = useState({ document_count: 0, chunk_count: 0, total_size_mb: 0 });
  const [isLoadingKb, setIsLoadingKb] = useState(false);
  const [showAddDocDialog, setShowAddDocDialog] = useState(false);
  const [newDocForm, setNewDocForm] = useState({ title: "", content_text: "", tags: "" });
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const [selectedDocForChunks, setSelectedDocForChunks] = useState(null);
  const [docChunks, setDocChunks] = useState([]);

  // AI Query Audit Log state
  const [aiQueries, setAiQueries] = useState([]);
  const [aiQueryStats, setAiQueryStats] = useState({ total_queries: 0, month_queries: 0, week_queries: 0, today_queries: 0, blocked_queries: 0, total_tokens_this_month: 0 });
  const [isLoadingQueries, setIsLoadingQueries] = useState(false);
  const [selectedQueryDetail, setSelectedQueryDetail] = useState(null);
  const [showQueryDetailDialog, setShowQueryDetailDialog] = useState(false);

  // AI Data Sources Config state
  const [dataSourcesConfig, setDataSourcesConfig] = useState(null);
  const [dataSourcesSchema, setDataSourcesSchema] = useState(null);
  const [dataSourcesForm, setDataSourcesForm] = useState({
    dashboard_data: { 
      monthly_summary: true, 
      monthly_summary_description: "Shows total income, total expenses, and net balance for the selected month. Use these exact figures when answering questions about totals.",
      payment_status: true, 
      payment_status_description: "Shows count of paid transactions vs pending (unpaid) transactions. Only report these exact counts, do not invent numbers.",
      current_month_context: true,
      current_month_context_description: "Indicates which month the user is currently viewing (e.g., February 2026). Use this to correctly identify the time period."
    },
    transaction_data: { 
      enabled: true, 
      enabled_description: "Master toggle for transaction data. When enabled, AI has access to the user's transaction list with dates, amounts, and details.",
      include_date: true,
      include_date_description: "Shows the date when each transaction occurred or is scheduled. Use this to filter and group transactions by time period.",
      include_name: true,
      include_name_description: "Shows the transaction name/title (e.g., 'Montevideo 619', 'Insurance', 'Kids Monthly'). Use these names when referring to specific transactions.",
      include_type: true,
      include_type_description: "Shows whether each transaction is an 'income' or 'expense'. Use this to calculate totals separately for income vs expenses.",
      include_category_names: true, 
      include_category_names_description: "Shows human-readable category names (e.g., 'Rent', 'Services', 'Legal') instead of internal IDs. Always refer to categories by their names.",
      include_original_amount: true,
      include_original_amount_description: "Shows the transaction amount in its original currency (e.g., ARS$1,000,000.00). Use this when the user asks about specific currency amounts.",
      include_converted_amount: true,
      include_converted_amount_description: "Shows the transaction amount converted to the user's preferred/base currency. Use this for calculating totals and comparisons.",
      include_balance: true,
      include_balance_description: "Shows the running balance after each transaction. Use this to show account balance at specific points in time.",
      include_status: true, 
      include_status_description: "Shows transaction status: 'Confirmed' (completed), 'Upcoming' (scheduled future), or 'Skipped'. Only count confirmed transactions for actual totals.",
      include_payment_status: true, 
      include_payment_status_description: "Shows whether each transaction is marked as 'Paid' or unpaid. Use this to count paid vs pending items.",
      include_recurring: true,
      include_recurring_description: "Shows if transaction repeats: 'Monthly', 'Weekly', 'Yearly', or one-time. Use this to identify recurring expenses.",
      include_next_due: true,
      include_next_due_description: "Shows the next due date for recurring transactions. Use this to inform users about upcoming payments.",
      include_descriptions: true, 
      include_descriptions_description: "Shows transaction notes/descriptions added by the user (e.g., 'Admin Fees'). Use these for additional context."
    },
    categories_data: { 
      enabled: true, 
      enabled_description: "Master toggle for category data. When enabled, AI can see all user-defined expense and income categories.",
      include_types: true,
      include_types_description: "Shows whether each category is for income or expense transactions.",
      include_icons_colors: false,
      include_icons_colors_description: "Shows the icon and color assigned to each category for visual identification."
    },
    goals_data: { 
      enabled: true, 
      enabled_description: "Master toggle for financial goals data. When enabled, AI can see the user's savings goals and targets.",
      include_progress: true,
      include_progress_description: "Shows current amount saved vs target amount for each goal. Use exact figures, do not estimate.",
      include_deadlines: true,
      include_deadlines_description: "Shows the target completion date for each goal."
    },
    user_memory: { 
      include_memories: true, 
      include_memories_description: "Personal notes/memories the user has saved about their financial situation (e.g., 'My salary arrives on the 15th').",
      include_objectives: true,
      include_objectives_description: "Financial objectives the user has set with target amounts and dates (e.g., 'Save $10,000 for emergency fund by December')."
    },
    filters: { 
      exclude_deleted: true, 
      exclude_deleted_description: "When enabled, deleted transactions are not included in calculations or responses.",
      exclude_skipped: true, 
      exclude_skipped_description: "When enabled, skipped recurring transactions are not included in calculations or responses.",
      default_time_range: "current_month",
      default_time_range_description: "The default time period for data queries when not specified by the user. Options: current_month, last_3_months, all_time."
    }
  });
  const [isSavingDataSources, setIsSavingDataSources] = useState(false);

  // Feedback & Coaching state
  const [feedbackList, setFeedbackList] = useState([]);
  const [coachingNotes, setCoachingNotes] = useState([]);
  const [feedbackFilter, setFeedbackFilter] = useState("all"); // all, positive, negative
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  
  // AI Simulation state
  const [simulationUsers, setSimulationUsers] = useState([]);
  const [selectedSimUser, setSelectedSimUser] = useState("");
  const [simulationQuery, setSimulationQuery] = useState("");
  const [simulationResponse, setSimulationResponse] = useState(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationHistory, setSimulationHistory] = useState([]);
  
  // Coaching note dialog
  const [showCoachingDialog, setShowCoachingDialog] = useState(false);
  const [newCoachingNote, setNewCoachingNote] = useState({ example_query: "", example_response: "", coaching_note: "", category: "general" });

  useEffect(() => {
    fetchData();
  }, []);
  
  useEffect(() => {
    if (smtpConfig) {
      setSmtpForm({ host: smtpConfig.host, port: smtpConfig.port.toString(), use_tls: smtpConfig.use_tls, username: smtpConfig.username, password: "", from_email: smtpConfig.from_email, from_name: smtpConfig.from_name, brand_name: smtpConfig.brand_name || "" });
    }
  }, [smtpConfig]);

  // Initialize AI config form when data loads
  useEffect(() => {
    if (aiConfig) {
      setAiConfigForm(prev => ({
        ...prev,
        default_model_id: aiConfig.default_model_id || "",
        fallback_model_ids: aiConfig.fallback_model_ids || [],
        pdf_processing_model_id: aiConfig.pdf_processing_model_id || "",
        // Legacy fields
        default_system_prompt: aiConfig.default_system_prompt || "",
        custom_system_prompt: aiConfig.custom_system_prompt || "",
        platform_help_guide: aiConfig.platform_help_guide || "",
        platform_help_enabled: aiConfig.platform_help_enabled !== false,
        // Individual (Personal) User AI Settings
        individual_default_system_prompt: aiConfig.individual_default_system_prompt || "",
        individual_custom_system_prompt: aiConfig.individual_custom_system_prompt || "",
        individual_platform_help_guide: aiConfig.individual_platform_help_guide || "",
        individual_platform_help_enabled: aiConfig.individual_platform_help_enabled !== false,
        // Business (Company) User AI Settings
        business_default_system_prompt: aiConfig.business_default_system_prompt || "",
        business_custom_system_prompt: aiConfig.business_custom_system_prompt || "",
        business_platform_help_guide: aiConfig.business_platform_help_guide || "",
        business_platform_help_enabled: aiConfig.business_platform_help_enabled !== false,
        enabled_ai_languages: aiConfig.enabled_ai_languages || ["en"],
        use_platform_translations: aiConfig.use_platform_translations !== false
      }));
    }
  }, [aiConfig]);

  // Fetch factory default prompt, default help guide, and available languages when AI tab is loaded
  useEffect(() => {
    const fetchFactoryDefault = async () => {
      try {
        const res = await api.get("/admin/ai/config/factory-default-prompt");
        setFactoryDefaultPrompt(res.data.factory_default_prompt || "");
      } catch (err) {
        console.error("Failed to fetch factory default prompt:", err);
      }
    };
    const fetchIndividualFactoryDefault = async () => {
      try {
        const res = await api.get("/admin/ai/config/factory-default-prompt/individual");
        setIndividualFactoryDefaultPrompt(res.data.factory_default_prompt || "");
      } catch (err) {
        console.error("Failed to fetch individual factory default prompt:", err);
      }
    };
    const fetchBusinessFactoryDefault = async () => {
      try {
        const res = await api.get("/admin/ai/config/factory-default-prompt/business");
        setBusinessFactoryDefaultPrompt(res.data.factory_default_prompt || "");
      } catch (err) {
        console.error("Failed to fetch business factory default prompt:", err);
      }
    };
    const fetchDefaultHelpGuide = async () => {
      try {
        const res = await api.get("/admin/ai/config/default-help-guide");
        setDefaultHelpGuide(res.data.default_help_guide || "");
      } catch (err) {
        console.error("Failed to fetch default help guide:", err);
      }
    };
    const fetchIndividualDefaultHelpGuide = async () => {
      try {
        const res = await api.get("/admin/ai/config/default-help-guide/individual");
        setIndividualDefaultHelpGuide(res.data.default_help_guide || "");
      } catch (err) {
        console.error("Failed to fetch individual default help guide:", err);
      }
    };
    const fetchBusinessDefaultHelpGuide = async () => {
      try {
        const res = await api.get("/admin/ai/config/default-help-guide/business");
        setBusinessDefaultHelpGuide(res.data.default_help_guide || "");
      } catch (err) {
        console.error("Failed to fetch business default help guide:", err);
      }
    };
    const fetchAvailableLanguages = async () => {
      try {
        const res = await api.get("/admin/ai/language-config");
        setAvailableLanguages(res.data.available_languages || []);
      } catch (err) {
        console.error("Failed to fetch available languages:", err);
      }
    };
    if (activeTab === "ai") {
      // Fetch legacy factory defaults
      if (!factoryDefaultPrompt) fetchFactoryDefault();
      if (!defaultHelpGuide) fetchDefaultHelpGuide();
      // Fetch individual factory defaults
      if (!individualFactoryDefaultPrompt) fetchIndividualFactoryDefault();
      if (!individualDefaultHelpGuide) fetchIndividualDefaultHelpGuide();
      // Fetch business factory defaults
      if (!businessFactoryDefaultPrompt) fetchBusinessFactoryDefault();
      if (!businessDefaultHelpGuide) fetchBusinessDefaultHelpGuide();
      // Fetch available languages
      if (availableLanguages.length === 0) fetchAvailableLanguages();
    }
  }, [activeTab, factoryDefaultPrompt, defaultHelpGuide, individualFactoryDefaultPrompt, businessFactoryDefaultPrompt, individualDefaultHelpGuide, businessDefaultHelpGuide, availableLanguages.length]);

  // Initialize thinking config form when data loads
  useEffect(() => {
    if (thinkingConfig) {
      setThinkingForm({
        intent_classification: thinkingConfig.intent_classification || { model_id: "", prompt_template: "", show_to_user: true },
        source_ranking: thinkingConfig.source_ranking || { model_id: "", user_data_weight: 70, admin_kb_weight: 60, web_search_weight: 40, max_sources: 8, show_to_user: true },
        response_generation: thinkingConfig.response_generation || { style: "professional_friendly", max_tokens: 2000, show_sources: true, temperature: 0.7 },
        web_search: thinkingConfig.web_search || { enabled: false, provider: "tavily", auto_search: false, max_results: 3 }
      });
    }
  }, [thinkingConfig]);

  // Initialize guardrails form when data loads
  useEffect(() => {
    if (guardrailsConfig) {
      setGuardrailsForm({
        prevent_off_topic: guardrailsConfig.prevent_off_topic,
        off_topic_message: guardrailsConfig.off_topic_message || "",
        block_harmful_content: guardrailsConfig.block_harmful_content,
        harmful_content_message: guardrailsConfig.harmful_content_message || "",
        max_response_length: guardrailsConfig.max_response_length || 2000,
        disable_response_length_limit: guardrailsConfig.disable_response_length_limit || false,
        length_exceeded_message: guardrailsConfig.length_exceeded_message || "",
        restrict_sensitive_data: guardrailsConfig.restrict_sensitive_data,
        sensitive_data_message: guardrailsConfig.sensitive_data_message || "",
        custom_restrictions: guardrailsConfig.custom_restrictions || []
      });
    }
  }, [guardrailsConfig]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [usersRes, packagesRes, smtpRes, languagesRes, aiConfigRes, thinkingRes, guardrailsRes] = await Promise.all([
        api.get("/admin/users"),
        api.get("/packages"),
        api.get("/admin/smtp").catch(() => ({ data: null })),
        api.get("/admin/languages").catch(() => ({ data: [] })),
        api.get("/admin/ai/config").catch(() => ({ data: null })),
        api.get("/admin/ai/thinking").catch(() => ({ data: null })),
        api.get("/admin/ai/guardrails").catch(() => ({ data: null }))
      ]);
      setUsers(usersRes.data);
      setPackages(packagesRes.data);
      setSmtpConfig(smtpRes.data);
      setLanguages(languagesRes.data || []);
      setAiConfig(aiConfigRes.data);
      setDisabledCompanyPages(aiConfigRes.data?.disabled_company_pages || []);
      setDisabledIndividualPages(aiConfigRes.data?.disabled_individual_pages || []);
      setThinkingConfig(thinkingRes.data);
      setGuardrailsConfig(guardrailsRes.data);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };
  
  // SMTP handlers
  const handleSaveSmtp = async (e) => {
    e.preventDefault();
    setIsSavingSmtp(true);
    try {
      await api.post("/admin/smtp", { ...smtpForm, port: parseInt(smtpForm.port) });
      toast.success("SMTP configuration saved!");
      fetchData();
      // Refresh brand name globally after saving
      refreshBrand();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save SMTP config");
    } finally {
      setIsSavingSmtp(false);
    }
  };
  
  const handleTestSmtp = async () => {
    if (!testEmail) return;
    setIsTesting(true);
    try {
      await api.post("/admin/smtp/test", { test_email: testEmail });
      toast.success("Test email sent to " + testEmail);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test email");
    } finally {
      setIsTesting(false);
    }
  };
  
  // Package handlers
  const handleOpenPackageDialog = (pkg) => {
    if (pkg) {
      setEditingPackage(pkg);
      setPackageForm({ 
        name: pkg.name, 
        description: pkg.description, 
        monthly_limit: pkg.monthly_limit.toString(), 
        sort_order: pkg.sort_order,
        ai_enabled: pkg.ai_enabled || false,
        ai_token_limit: pkg.ai_token_limit || 0,
        tavily_token_limit: pkg.tavily_token_limit || 0,
        perplexity_token_limit: pkg.perplexity_token_limit || 0
      });
    } else {
      setEditingPackage(null);
      setPackageForm({ 
        name: "", 
        description: "", 
        monthly_limit: "", 
        sort_order: 0,
        ai_enabled: false,
        ai_token_limit: 0,
        tavily_token_limit: 0,
        perplexity_token_limit: 0
      });
    }
    setShowPackageDialog(true);
  };
  
  const handleSavePackage = async (e) => {
    e.preventDefault();
    if (!packageForm.name || !packageForm.monthly_limit) return;
    setIsSavingPackage(true);
    try {
      const payload = { 
        ...packageForm, 
        monthly_limit: parseInt(packageForm.monthly_limit), 
        sort_order: parseInt(packageForm.sort_order) || 0,
        ai_token_limit: parseInt(packageForm.ai_token_limit) || 0,
        tavily_token_limit: parseInt(packageForm.tavily_token_limit) || 0,
        perplexity_token_limit: parseInt(packageForm.perplexity_token_limit) || 0
      };
      if (editingPackage) {
        await api.put("/admin/packages/" + editingPackage.id, payload);
        toast.success("Package updated!");
      } else {
        await api.post("/admin/packages", payload);
        toast.success("Package created!");
      }
      setShowPackageDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save package");
    } finally {
      setIsSavingPackage(false);
    }
  };
  
  const handleDeletePackage = async (id) => {
    try {
      await api.delete("/admin/packages/" + id);
      toast.success("Package deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete package");
    }
  };
  
  // User overview handler
  const handleViewUser = async (userId) => {
    setSelectedUserId(userId);
    setIsLoadingOverview(true);
    try {
      const res = await api.get("/admin/users/" + userId + "/overview");
      setUserOverview(res.data);
    } catch (err) {
      toast.error("Failed to load user overview");
    } finally {
      setIsLoadingOverview(false);
    }
  };

  // Delete user handler
  const handleDeleteUser = async (userId, userEmail) => {
    const confirmed = window.confirm(
      `⚠️ WARNING: This will PERMANENTLY delete the user "${userEmail}" and ALL their data including:\n\n` +
      `• Transactions & Categories\n` +
      `• Goals & Contributions\n` +
      `• Clients, Products & Sales\n` +
      `• Invoices & Time Entries\n` +
      `• Projects & All Settings\n\n` +
      `This action CANNOT be undone.\n\n` +
      `Are you sure you want to proceed?`
    );
    
    if (!confirmed) return;
    
    // Double confirmation for safety
    const doubleConfirmed = window.confirm(
      `FINAL CONFIRMATION:\n\nYou are about to permanently delete:\n${userEmail}\n\nType "DELETE" mentally and click OK to confirm.`
    );
    
    if (!doubleConfirmed) return;
    
    try {
      const res = await api.delete("/admin/users/" + userId);
      toast.success(`User ${userEmail} and all their data has been permanently deleted`);
      console.log("Deletion stats:", res.data.deletion_stats);
      fetchData(); // Refresh users list
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    }
  };
  
  // Language handlers
  const fetchTranslationsForLanguage = async (langCode) => {
    setIsLoadingTranslations(true);
    try {
      const res = await api.get(`/admin/translations?language_code=${langCode}`);
      setTranslations(res.data);
    } catch (err) {
      toast.error("Failed to load translations");
    } finally {
      setIsLoadingTranslations(false);
    }
  };

  const handleToggleLanguage = async (langCode, enabled) => {
    try {
      await api.put(`/admin/languages/${langCode}`, { is_enabled: enabled });
      setLanguages(languages.map(l => l.code === langCode ? { ...l, is_enabled: enabled } : l));
      toast.success(`Language ${enabled ? "enabled" : "disabled"}`);
    } catch (err) {
      toast.error("Failed to update language");
    }
  };

  const handleSaveTranslations = async () => {
    if (Object.keys(editedTranslations).length === 0) return;
    
    setIsSavingTranslations(true);
    try {
      const updates = Object.entries(editedTranslations).map(([id, value]) => ({ id, value }));
      await api.post("/admin/translations/bulk", { translations: updates });
      
      // Update local state
      setTranslations(translations.map(t => 
        editedTranslations[t.id] !== undefined 
          ? { ...t, value: editedTranslations[t.id], is_custom: true } 
          : t
      ));
      setEditedTranslations({});
      
      // Refresh the frontend translation cache
      refreshTranslations();
      
      toast.success("Translations saved!");
    } catch (err) {
      toast.error("Failed to save translations");
    } finally {
      setIsSavingTranslations(false);
    }
  };

  const handleResetTranslation = async (translationId) => {
    try {
      await api.post(`/admin/translations/reset/${translationId}`);
      // Update local state
      setTranslations(translations.map(t => 
        t.id === translationId ? { ...t, value: t.default_value, is_custom: false } : t
      ));
      // Remove from edited if present
      const newEdited = { ...editedTranslations };
      delete newEdited[translationId];
      setEditedTranslations(newEdited);
      
      refreshTranslations();
      toast.success("Translation reset to default");
    } catch (err) {
      toast.error("Failed to reset translation");
    }
  };

  // Seed translations handler
  const [isSeedingTranslations, setIsSeedingTranslations] = useState(false);
  
  const handleSeedTranslations = async () => {
    setIsSeedingTranslations(true);
    try {
      await api.post("/admin/translations/seed");
      toast.success("Translations seeded successfully! All missing translations have been added.");
      // Refresh the languages list
      const languagesRes = await api.get("/admin/languages").catch(() => ({ data: [] }));
      setLanguages(languagesRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to seed translations");
    } finally {
      setIsSeedingTranslations(false);
    }
  };

  const getFilteredTranslations = () => {
    let filtered = translations;
    
    if (selectedNamespace !== "all") {
      filtered = filtered.filter(t => t.namespace === selectedNamespace);
    }
    
    if (translationSearch) {
      const search = translationSearch.toLowerCase();
      filtered = filtered.filter(t => 
        t.key.toLowerCase().includes(search) || 
        t.value.toLowerCase().includes(search) ||
        t.default_value.toLowerCase().includes(search)
      );
    }
    
    return filtered;
  };

  // AI Configuration handlers
  const fetchAiModels = async () => {
    setIsLoadingAiModels(true);
    try {
      const res = await api.get("/admin/ai/models");
      setAiModels(res.data.models || []);
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error("Please configure OpenRouter API key first");
      } else {
        toast.error("Failed to fetch models");
      }
    } finally {
      setIsLoadingAiModels(false);
    }
  };

  const handleRefreshModels = async () => {
    setIsLoadingAiModels(true);
    try {
      const res = await api.get("/admin/ai/models/refresh");
      toast.success(res.data.message);
      await fetchAiModels();
    } catch (err) {
      toast.error("Failed to refresh models");
    } finally {
      setIsLoadingAiModels(false);
    }
  };

  const handleSaveAiConfig = async () => {
    setIsSavingAiConfig(true);
    try {
      // Only send non-empty API keys
      const payload = {};
      if (aiConfigForm.openrouter_api_key) payload.openrouter_api_key = aiConfigForm.openrouter_api_key;
      if (aiConfigForm.openrouter_provisioning_key) payload.openrouter_provisioning_key = aiConfigForm.openrouter_provisioning_key;
      if (aiConfigForm.tavily_api_key) payload.tavily_api_key = aiConfigForm.tavily_api_key;
      if (aiConfigForm.perplexity_api_key) payload.perplexity_api_key = aiConfigForm.perplexity_api_key;
      if (aiConfigForm.default_model_id) payload.default_model_id = aiConfigForm.default_model_id;
      if (aiConfigForm.fallback_model_ids) payload.fallback_model_ids = aiConfigForm.fallback_model_ids;
      if (aiConfigForm.pdf_processing_model_id) payload.pdf_processing_model_id = aiConfigForm.pdf_processing_model_id;

      await api.put("/admin/ai/config", payload);
      toast.success("AI configuration saved!");
      
      // Clear API key fields after save
      setAiConfigForm(prev => ({
        ...prev,
        openrouter_api_key: "",
        openrouter_provisioning_key: "",
        tavily_api_key: "",
        perplexity_api_key: ""
      }));
      
      // Refresh config
      const res = await api.get("/admin/ai/config");
      setAiConfig(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save AI config");
    } finally {
      setIsSavingAiConfig(false);
    }
  };

  const handleValidateApiKey = async () => {
    try {
      const res = await api.post("/admin/ai/config/validate");
      if (res.data.valid) {
        toast.success(res.data.message);
        await fetchAiModels();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error("Failed to validate API key");
    }
  };

  const handleSetDefaultModel = async (modelId) => {
    try {
      await api.put(`/admin/ai/models/default?model_id=${modelId}`);
      setAiConfigForm(prev => ({ ...prev, default_model_id: modelId }));
      setAiConfig(prev => ({ ...prev, default_model_id: modelId }));
      toast.success("Default model updated");
    } catch (err) {
      toast.error("Failed to set default model");
    }
  };

  const handleToggleFallbackModel = async (modelId) => {
    const currentFallbacks = aiConfigForm.fallback_model_ids || [];
    let newFallbacks;
    
    if (currentFallbacks.includes(modelId)) {
      newFallbacks = currentFallbacks.filter(id => id !== modelId);
    } else {
      if (currentFallbacks.length >= 5) {
        toast.error("Maximum 5 fallback models allowed");
        return;
      }
      newFallbacks = [...currentFallbacks, modelId];
    }
    
    try {
      await api.put("/admin/ai/models/fallback", newFallbacks);
      setAiConfigForm(prev => ({ ...prev, fallback_model_ids: newFallbacks }));
      setAiConfig(prev => ({ ...prev, fallback_model_ids: newFallbacks }));
      toast.success("Fallback models updated");
    } catch (err) {
      toast.error("Failed to update fallback models");
    }
  };

  const handleSetPdfModel = async (modelId) => {
    try {
      await api.put(`/admin/ai/models/pdf?model_id=${modelId}`);
      setAiConfigForm(prev => ({ ...prev, pdf_processing_model_id: modelId }));
      setAiConfig(prev => ({ ...prev, pdf_processing_model_id: modelId }));
      toast.success("PDF processing model updated");
    } catch (err) {
      toast.error("Failed to set PDF model");
    }
  };

  // Thinking Layer handlers
  const handleSaveThinking = async () => {
    setIsSavingThinking(true);
    try {
      await api.put("/admin/ai/thinking", thinkingForm);
      toast.success("Thinking layer configuration saved!");
      const res = await api.get("/admin/ai/thinking");
      setThinkingConfig(res.data);
    } catch (err) {
      toast.error("Failed to save thinking config");
    } finally {
      setIsSavingThinking(false);
    }
  };

  // Guardrails handlers
  const handleSaveGuardrails = async () => {
    setIsSavingGuardrails(true);
    try {
      await api.put("/admin/ai/guardrails", guardrailsForm);
      toast.success("Guardrails configuration saved!");
      const res = await api.get("/admin/ai/guardrails");
      setGuardrailsConfig(res.data);
    } catch (err) {
      toast.error("Failed to save guardrails config");
    } finally {
      setIsSavingGuardrails(false);
    }
  };

  const handleAddRestriction = async () => {
    if (!newRestriction.pattern || !newRestriction.message) {
      toast.error("Please enter both pattern and message");
      return;
    }
    
    try {
      const res = await api.post(`/admin/ai/guardrails/restrictions?pattern=${encodeURIComponent(newRestriction.pattern)}&message=${encodeURIComponent(newRestriction.message)}`);
      setGuardrailsForm(prev => ({
        ...prev,
        custom_restrictions: [...prev.custom_restrictions, res.data.restriction]
      }));
      setNewRestriction({ pattern: "", message: "" });
      toast.success("Custom restriction added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add restriction");
    }
  };

  const handleDeleteRestriction = async (restrictionId) => {
    try {
      await api.delete(`/admin/ai/guardrails/restrictions/${restrictionId}`);
      setGuardrailsForm(prev => ({
        ...prev,
        custom_restrictions: prev.custom_restrictions.filter(r => r.id !== restrictionId)
      }));
      toast.success("Restriction deleted");
    } catch (err) {
      toast.error("Failed to delete restriction");
    }
  };

  // Company Pages Visibility handlers (Admin-controlled)
  const COMPANY_PAGES = [
    { id: "analytics", name: "Analytics", icon: BarChart3, category: "Reporting & Analytics", description: "Sales performance charts and insights" },
    { id: "cash-flow", name: "Cash Flow", icon: Wallet, category: "Finance", description: "Cash flow forecasting and tracking" },
    { id: "payment-aging", name: "Payment Aging", icon: Clock, category: "Business Intelligence", description: "Overdue payment tracking and reports" },
    { id: "tax-management", name: "Tax Management", icon: Receipt, category: "Business Intelligence", description: "Tax categorization and export" },
    { id: "break-even", name: "Break-even", icon: Calculator, category: "Business Intelligence", description: "Profitability analysis tools" },
    { id: "inventory", name: "Inventory", icon: Boxes, category: "Industry (Product)", description: "Stock management for product businesses" },
    { id: "time-tracking", name: "Time Tracking", icon: Clock, category: "Industry (Service)", description: "Hours tracking for service businesses" },
    { id: "product-governance", name: "Product Governance Matrix", icon: ClipboardList, category: "Project Management", description: "Track requirements, improvements, and issues" }
  ];

  // Individual Pages Visibility handlers (Admin-controlled)
  const INDIVIDUAL_PAGES = [
    { id: "product-governance", name: "Product Governance Matrix", icon: ClipboardList, description: "Track requirements, improvements, and issues" }
  ];

  const handleToggleCompanyPage = async (pageId) => {
    try {
      const newDisabledPages = disabledCompanyPages.includes(pageId)
        ? disabledCompanyPages.filter(id => id !== pageId)
        : [...disabledCompanyPages, pageId];
      
      await api.put("/admin/ai/config", { disabled_company_pages: newDisabledPages });
      setDisabledCompanyPages(newDisabledPages);
      const pageName = COMPANY_PAGES.find(p => p.id === pageId)?.name || pageId;
      toast.success(disabledCompanyPages.includes(pageId) 
        ? `${pageName} enabled for company users`
        : `${pageName} disabled for company users`
      );
    } catch (err) {
      toast.error("Failed to update page visibility");
    }
  };

  const handleEnableAllCompanyPages = async () => {
    try {
      await api.put("/admin/ai/config", { disabled_company_pages: [] });
      setDisabledCompanyPages([]);
      toast.success("All company pages enabled");
    } catch (err) {
      toast.error("Failed to enable all pages");
    }
  };

  const handleToggleIndividualPage = async (pageId) => {
    try {
      const newDisabledPages = disabledIndividualPages.includes(pageId)
        ? disabledIndividualPages.filter(id => id !== pageId)
        : [...disabledIndividualPages, pageId];
      
      await api.put("/admin/ai/config", { disabled_individual_pages: newDisabledPages });
      setDisabledIndividualPages(newDisabledPages);
      const pageName = INDIVIDUAL_PAGES.find(p => p.id === pageId)?.name || pageId;
      toast.success(disabledIndividualPages.includes(pageId) 
        ? `${pageName} enabled for individual users`
        : `${pageName} disabled for individual users`
      );
    } catch (err) {
      toast.error("Failed to update page visibility");
    }
  };

  const handleEnableAllIndividualPages = async () => {
    try {
      await api.put("/admin/ai/config", { disabled_individual_pages: [] });
      setDisabledIndividualPages([]);
      toast.success("All individual pages enabled");
    } catch (err) {
      toast.error("Failed to enable all pages");
    }
  };

  // Knowledge Base handlers
  const fetchKbData = async () => {
    setIsLoadingKb(true);
    try {
      const [docsRes, statsRes] = await Promise.all([
        api.get("/admin/kb/documents"),
        api.get("/admin/kb/stats")
      ]);
      setKbDocuments(docsRes.data || []);
      setKbStats(statsRes.data || { document_count: 0, chunk_count: 0, total_size_mb: 0 });
    } catch (err) {
      toast.error("Failed to load knowledge base");
    } finally {
      setIsLoadingKb(false);
    }
  };

  // AI Query Audit functions
  const fetchAiQueries = async () => {
    setIsLoadingQueries(true);
    try {
      const [queriesRes, statsRes] = await Promise.all([
        api.get("/admin/ai/queries?limit=50"),
        api.get("/admin/ai/queries/stats")
      ]);
      setAiQueries(queriesRes.data || []);
      setAiQueryStats(statsRes.data || { total_queries: 0, month_queries: 0, week_queries: 0, today_queries: 0, blocked_queries: 0, total_tokens_this_month: 0 });
    } catch (err) {
      console.error("Failed to load AI queries", err);
    } finally {
      setIsLoadingQueries(false);
    }
  };

  const handleViewQueryDetail = async (queryId) => {
    try {
      const res = await api.get(`/admin/ai/queries/${queryId}`);
      setSelectedQueryDetail(res.data);
      setShowQueryDetailDialog(true);
    } catch (err) {
      toast.error("Failed to load query details");
    }
  };

  // AI Data Sources Config functions
  const fetchDataSourcesConfig = async () => {
    try {
      const res = await api.get("/admin/ai/data-sources");
      setDataSourcesConfig(res.data);
      // Preserve all fields including descriptions
      setDataSourcesForm({
        dashboard_data: {
          monthly_summary: res.data.dashboard_data?.monthly_summary ?? true,
          monthly_summary_description: res.data.dashboard_data?.monthly_summary_description || "Shows total income, total expenses, and net balance for the selected month.",
          payment_status: res.data.dashboard_data?.payment_status ?? true,
          payment_status_description: res.data.dashboard_data?.payment_status_description || "Shows count of paid transactions vs pending (unpaid) transactions.",
          current_month_context: res.data.dashboard_data?.current_month_context ?? true,
          current_month_context_description: res.data.dashboard_data?.current_month_context_description || "Indicates which month the user is currently viewing."
        },
        transaction_data: {
          enabled: res.data.transaction_data?.enabled ?? true,
          enabled_description: res.data.transaction_data?.enabled_description || "Master toggle for transaction data.",
          include_date: res.data.transaction_data?.include_date ?? true,
          include_date_description: res.data.transaction_data?.include_date_description || "Shows transaction dates.",
          include_name: res.data.transaction_data?.include_name ?? true,
          include_name_description: res.data.transaction_data?.include_name_description || "Shows transaction names.",
          include_type: res.data.transaction_data?.include_type ?? true,
          include_type_description: res.data.transaction_data?.include_type_description || "Shows income/expense type.",
          include_category_names: res.data.transaction_data?.include_category_names ?? true,
          include_category_names_description: res.data.transaction_data?.include_category_names_description || "Shows human-readable category names.",
          include_original_amount: res.data.transaction_data?.include_original_amount ?? true,
          include_original_amount_description: res.data.transaction_data?.include_original_amount_description || "Shows original currency amount.",
          include_converted_amount: res.data.transaction_data?.include_converted_amount ?? true,
          include_converted_amount_description: res.data.transaction_data?.include_converted_amount_description || "Shows converted amount.",
          include_balance: res.data.transaction_data?.include_balance ?? true,
          include_balance_description: res.data.transaction_data?.include_balance_description || "Shows running balance.",
          include_status: res.data.transaction_data?.include_status ?? true,
          include_status_description: res.data.transaction_data?.include_status_description || "Shows transaction status.",
          include_payment_status: res.data.transaction_data?.include_payment_status ?? true,
          include_payment_status_description: res.data.transaction_data?.include_payment_status_description || "Shows paid/unpaid status.",
          include_recurring: res.data.transaction_data?.include_recurring ?? true,
          include_recurring_description: res.data.transaction_data?.include_recurring_description || "Shows recurring frequency.",
          include_next_due: res.data.transaction_data?.include_next_due ?? true,
          include_next_due_description: res.data.transaction_data?.include_next_due_description || "Shows next due date.",
          include_descriptions: res.data.transaction_data?.include_descriptions ?? true,
          include_descriptions_description: res.data.transaction_data?.include_descriptions_description || "Shows transaction notes."
        },
        categories_data: {
          enabled: res.data.categories_data?.enabled ?? true,
          enabled_description: res.data.categories_data?.enabled_description || "Master toggle for category data.",
          include_types: res.data.categories_data?.include_types ?? true,
          include_types_description: res.data.categories_data?.include_types_description || "Shows income/expense type.",
          include_icons_colors: res.data.categories_data?.include_icons_colors ?? false,
          include_icons_colors_description: res.data.categories_data?.include_icons_colors_description || "Shows category icons and colors."
        },
        goals_data: {
          enabled: res.data.goals_data?.enabled ?? true,
          enabled_description: res.data.goals_data?.enabled_description || "Master toggle for goals data.",
          include_progress: res.data.goals_data?.include_progress ?? true,
          include_progress_description: res.data.goals_data?.include_progress_description || "Shows goal progress.",
          include_deadlines: res.data.goals_data?.include_deadlines ?? true,
          include_deadlines_description: res.data.goals_data?.include_deadlines_description || "Shows goal deadlines."
        },
        user_memory: {
          include_memories: res.data.user_memory?.include_memories ?? true,
          include_memories_description: res.data.user_memory?.include_memories_description || "Personal notes from user.",
          include_objectives: res.data.user_memory?.include_objectives ?? true,
          include_objectives_description: res.data.user_memory?.include_objectives_description || "Financial objectives from user."
        },
        filters: {
          exclude_deleted: res.data.filters?.exclude_deleted ?? true,
          exclude_deleted_description: res.data.filters?.exclude_deleted_description || "Excludes deleted transactions.",
          exclude_skipped: res.data.filters?.exclude_skipped ?? true,
          exclude_skipped_description: res.data.filters?.exclude_skipped_description || "Excludes skipped transactions.",
          default_time_range: res.data.filters?.default_time_range || "current_month",
          default_time_range_description: res.data.filters?.default_time_range_description || "Default time period for queries."
        }
      });
    } catch (err) {
      console.error("Failed to load data sources config", err);
    }
  };

  // Fetch schema for dynamic UI rendering
  const fetchDataSourcesSchema = async () => {
    try {
      const res = await api.get("/admin/ai/data-sources/schema");
      setDataSourcesSchema(res.data.schema);
    } catch (err) {
      console.error("Failed to load data sources schema", err);
    }
  };

  // Helper to count enabled fields from schema
  const countEnabledFieldsFromSchema = () => {
    if (!dataSourcesSchema) return 0;
    let count = 0;
    
    Object.entries(dataSourcesSchema).forEach(([categoryKey, category]) => {
      const categoryData = dataSourcesForm[categoryKey];
      if (!categoryData) return;
      
      const hasMasterToggle = category.has_master_toggle;
      const masterEnabled = hasMasterToggle ? categoryData[category.master_toggle_field] : true;
      
      Object.entries(category.fields).forEach(([fieldKey, field]) => {
        if (field.type === "select") return; // Don't count select fields
        
        const dependsOn = field.depends_on;
        const isEnabled = categoryData[fieldKey];
        
        if (dependsOn) {
          // Field depends on master toggle
          if (masterEnabled && isEnabled) count++;
        } else {
          // Independent field
          if (isEnabled) count++;
        }
      });
    });
    
    return count;
  };

  // Helper to build placeholder preview from schema
  const buildPlaceholderPreviewFromSchema = () => {
    if (!dataSourcesSchema) return [];
    const preview = [];
    
    Object.entries(dataSourcesSchema).forEach(([categoryKey, category]) => {
      const categoryData = dataSourcesForm[categoryKey];
      if (!categoryData) return;
      
      const hasMasterToggle = category.has_master_toggle;
      const masterEnabled = hasMasterToggle ? categoryData[category.master_toggle_field] : true;
      
      Object.entries(category.fields).forEach(([fieldKey, field]) => {
        if (field.type === "select") return; // Skip select fields
        
        const dependsOn = field.depends_on;
        const isEnabled = categoryData[fieldKey];
        const description = categoryData[`${fieldKey}_description`] || field.default_description;
        
        if (dependsOn) {
          // Field depends on master toggle
          if (masterEnabled && isEnabled) {
            preview.push({
              label: field.label,
              description,
              indent: true,
              category: category.label
            });
          }
        } else {
          // Independent or master field
          if (isEnabled) {
            preview.push({
              label: field.label,
              description,
              indent: false,
              category: category.label,
              isMaster: field.type === "master_toggle"
            });
          }
        }
      });
    });
    
    return preview;
  };

  const handleSaveDataSources = async () => {
    setIsSavingDataSources(true);
    try {
      await api.put("/admin/ai/data-sources", dataSourcesForm);
      toast.success("Data sources configuration saved!");
      fetchDataSourcesConfig();
    } catch (err) {
      toast.error("Failed to save data sources config");
    } finally {
      setIsSavingDataSources(false);
    }
  };

  const handleCreateTextDocument = async () => {
    if (!newDocForm.title || !newDocForm.content_text) {
      toast.error("Title and content are required");
      return;
    }
    
    setIsUploadingDoc(true);
    try {
      const tags = newDocForm.tags.split(",").map(t => t.trim()).filter(Boolean);
      await api.post("/admin/kb/documents/text", {
        title: newDocForm.title,
        content_text: newDocForm.content_text,
        tags
      });
      toast.success("Document created and processed");
      setShowAddDocDialog(false);
      setNewDocForm({ title: "", content_text: "", tags: "" });
      await fetchKbData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create document");
    } finally {
      setIsUploadingDoc(false);
    }
  };

  const handleUploadPdf = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Only PDF files are supported");
      return;
    }
    
    setIsUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", file.name.replace('.pdf', ''));
      formData.append("tags", "");
      formData.append("extraction_method", "hybrid");
      
      await api.post("/admin/kb/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("PDF uploaded and processed");
      await fetchKbData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload PDF");
    } finally {
      setIsUploadingDoc(false);
      event.target.value = "";
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!confirm("Delete this document and all its chunks?")) return;
    
    try {
      await api.delete(`/admin/kb/documents/${docId}`);
      toast.success("Document deleted");
      setKbDocuments(prev => prev.filter(d => d.id !== docId));
      setKbStats(prev => ({ ...prev, document_count: prev.document_count - 1 }));
    } catch (err) {
      toast.error("Failed to delete document");
    }
  };

  const handleViewChunks = async (docId) => {
    try {
      const res = await api.get(`/admin/kb/documents/${docId}/chunks`);
      setDocChunks(res.data.chunks || []);
      setSelectedDocForChunks(docId);
    } catch (err) {
      toast.error("Failed to load chunks");
    }
  };

  // ===================== FEEDBACK & COACHING FUNCTIONS =====================
  
  const fetchFeedback = async () => {
    setIsLoadingFeedback(true);
    try {
      const params = feedbackFilter !== "all" ? { rating: feedbackFilter } : {};
      const res = await api.get("/admin/ai/feedback", { params });
      setFeedbackList(res.data || []);
    } catch (err) {
      toast.error("Failed to load feedback");
    } finally {
      setIsLoadingFeedback(false);
    }
  };

  const fetchCoachingNotes = async () => {
    try {
      const res = await api.get("/admin/ai/coaching");
      setCoachingNotes(res.data || []);
    } catch (err) {
      toast.error("Failed to load coaching notes");
    }
  };

  const handlePromoteToCoaching = async (feedbackId) => {
    try {
      await api.post(`/admin/ai/feedback/${feedbackId}/promote`);
      toast.success("Promoted to coaching note!");
      fetchFeedback();
      fetchCoachingNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to promote");
    }
  };

  const handleCreateCoachingNote = async () => {
    if (!newCoachingNote.example_query || !newCoachingNote.example_response || !newCoachingNote.coaching_note) {
      toast.error("Please fill all required fields");
      return;
    }
    try {
      await api.post("/admin/ai/coaching", newCoachingNote);
      toast.success("Coaching note created!");
      setShowCoachingDialog(false);
      setNewCoachingNote({ example_query: "", example_response: "", coaching_note: "", category: "general" });
      fetchCoachingNotes();
    } catch (err) {
      toast.error("Failed to create coaching note");
    }
  };

  const handleDeleteCoachingNote = async (noteId) => {
    if (!confirm("Delete this coaching note?")) return;
    try {
      await api.delete(`/admin/ai/coaching/${noteId}`);
      toast.success("Coaching note deleted");
      setCoachingNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (err) {
      toast.error("Failed to delete coaching note");
    }
  };

  // ===================== AI SIMULATION FUNCTIONS =====================

  const fetchSimulationUsers = async () => {
    try {
      const res = await api.get("/admin/ai/simulation/users");
      setSimulationUsers(res.data || []);
    } catch (err) {
      console.error("Failed to load users for simulation");
    }
  };

  const handleRunSimulation = async () => {
    if (!selectedSimUser || !simulationQuery.trim()) {
      toast.error("Please select a user and enter a query");
      return;
    }
    setIsSimulating(true);
    setSimulationResponse(null);
    try {
      const res = await api.post("/admin/ai/simulation/chat", {
        simulated_user_id: selectedSimUser,
        query: simulationQuery,
        include_thinking: true
      });
      setSimulationResponse(res.data);
      setSimulationHistory(prev => [...prev, {
        query: simulationQuery,
        response: res.data.response,
        user_email: res.data.simulated_user_email,
        timestamp: new Date().toISOString()
      }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Simulation failed");
    } finally {
      setIsSimulating(false);
    }
  };

  // Filter models based on search
  const getFilteredModels = () => {
    if (!modelSearchQuery) return aiModels;
    const search = modelSearchQuery.toLowerCase();
    return aiModels.filter(m => 
      m.id.toLowerCase().includes(search) || 
      m.name.toLowerCase().includes(search) ||
      (m.description && m.description.toLowerCase().includes(search))
    );
  };
  
  const selectedUser = users.find(u => u.id === selectedUserId);

  return (
    <div className="min-h-screen bg-background admin-theme">
      <header className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur">
        <div className="max-w-[1200px] mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="size-8 rounded-lg bg-[hsl(var(--ff-blue-steel))] flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            <span className="font-semibold text-lg">{brandName} Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{user?.email}</span>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="admin-logout-button">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="admin-tabs">
          <TabsList className="grid w-full grid-cols-6 mb-6">
            <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="h-4 w-4 mr-2" /> Users</TabsTrigger>
            <TabsTrigger value="packages" data-testid="admin-tab-packages"><Package className="h-4 w-4 mr-2" /> Packages</TabsTrigger>
            <TabsTrigger value="smtp" data-testid="admin-tab-smtp"><Mail className="h-4 w-4 mr-2" /> SMTP</TabsTrigger>
            <TabsTrigger value="languages" data-testid="admin-tab-languages"><Globe className="h-4 w-4 mr-2" /> Languages</TabsTrigger>
            <TabsTrigger value="ai" data-testid="admin-tab-ai" onClick={() => { if (aiModels.length === 0 && aiConfig?.has_openrouter_key) fetchAiModels(); if (kbDocuments.length === 0) fetchKbData(); if (aiQueries.length === 0) fetchAiQueries(); if (!dataSourcesConfig) fetchDataSourcesConfig(); if (!dataSourcesSchema) fetchDataSourcesSchema(); }}><Bot className="h-4 w-4 mr-2" /> AI Agent</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="admin-tab-feedback" onClick={() => { if (feedbackList.length === 0) fetchFeedback(); if (coachingNotes.length === 0) fetchCoachingNotes(); if (simulationUsers.length === 0) fetchSimulationUsers(); }}><MessageSquare className="h-4 w-4 mr-2" /> Feedback</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <Card data-testid="admin-users-panel">
              <CardHeader>
                <CardTitle>Users Management</CardTitle>
                <CardDescription>View and manage all registered users</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
                ) : users.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">No users registered yet</p>
                  </div>
                ) : (
                  <Table data-testid="admin-users-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Package</TableHead>
                        <TableHead>Tx Usage</TableHead>
                        <TableHead>AI Tokens (Month)</TableHead>
                        <TableHead>AI Tokens (Total)</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map(u => (
                        <TableRow key={u.id} data-testid={"admin-user-row-" + u.id}>
                          <TableCell className="font-medium">{u.email}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={u.user_type === "company" ? "bg-blue-500/10 text-blue-600 border-blue-500/30" : "bg-gray-500/10 text-gray-600 border-gray-500/30"}>
                              {u.user_type === "company" ? (
                                <><Building className="h-3 w-3 mr-1" /> Business</>
                              ) : (
                                <><Users className="h-3 w-3 mr-1" /> Personal</>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell><Badge variant="outline">{u.package_name || "No Package"}</Badge></TableCell>
                          <TableCell>
                            {u.monthly_limit !== null && u.monthly_limit !== undefined ? (
                              u.monthly_limit === 0 ? (
                                <span className="text-sm text-muted-foreground">Unlimited ({u.monthly_usage} used)</span>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <Progress value={(u.monthly_usage / u.monthly_limit) * 100} className="w-20 h-2" />
                                  <span className="text-xs text-muted-foreground">{u.monthly_usage}/{u.monthly_limit}</span>
                                </div>
                              )
                            ) : <span className="text-muted-foreground">-</span>}
                          </TableCell>
                          <TableCell>
                            {u.token_limit ? (
                              <div className="flex items-center gap-2">
                                <Progress value={(u.month_tokens_used / u.token_limit) * 100} className="w-16 h-2" />
                                <span className="text-xs text-muted-foreground">{(u.month_tokens_used || 0).toLocaleString()}/{u.token_limit.toLocaleString()}</span>
                              </div>
                            ) : (
                              <span className="text-sm">{(u.month_tokens_used || 0).toLocaleString()} <span className="text-muted-foreground text-xs">(Unlimited)</span></span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <span className="font-medium">{(u.total_tokens_used || 0).toLocaleString()}</span>
                              <span className="text-xs text-muted-foreground ml-1">({u.total_queries || 0} queries)</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{format(parseISO(u.created_at), "MMM d, yyyy")}</TableCell>
                          <TableCell><Badge variant="outline" className={u.is_active ? "status-paid" : "status-pending"}>{u.is_active ? "Active" : "Inactive"}</Badge></TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleViewUser(u.id)} data-testid={"view-user-" + u.id}>
                                <Eye className="h-4 w-4 mr-1" /> View
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDeleteUser(u.id, u.email)} 
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                data-testid={"delete-user-" + u.id}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="packages">
            <Card data-testid="admin-packages-panel">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Packages Management</CardTitle>
                  <CardDescription>Create and manage subscription packages</CardDescription>
                </div>
                <Button onClick={() => handleOpenPackageDialog(null)} data-testid="create-package-button">
                  <Plus className="h-4 w-4 mr-2" /> Create Package
                </Button>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full" />)}</div>
                ) : packages.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">No packages created yet</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {packages.map(pkg => (
                      <Card key={pkg.id} data-testid={"package-card-" + pkg.id}>
                        <CardContent className="p-4 flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold">{pkg.name}</p>
                              <Badge variant="outline">{pkg.monthly_limit === 0 ? "Unlimited" : pkg.monthly_limit + " tx/mo"}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{pkg.description}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="icon" onClick={() => handleOpenPackageDialog(pkg)} data-testid={"edit-package-" + pkg.id}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeletePackage(pkg.id)} data-testid={"delete-package-" + pkg.id}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="smtp">
            <Card data-testid="admin-smtp-panel">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Server className="h-5 w-5" /> SMTP Configuration</CardTitle>
                <CardDescription>Configure email settings for OTP delivery</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="space-y-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
                ) : (
                  <form onSubmit={handleSaveSmtp} className="space-y-6">
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted">
                      {smtpConfig ? (
                        <><Check className="h-5 w-5 text-[hsl(var(--ff-success))]" /><span className="text-sm">SMTP is configured</span></>
                      ) : (
                        <><AlertCircle className="h-5 w-5 text-[hsl(var(--ff-warning))]" /><span className="text-sm">SMTP not configured - OTP emails will not be sent</span></>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>SMTP Host</Label>
                        <Input value={smtpForm.host} onChange={e => setSmtpForm({...smtpForm, host: e.target.value})} placeholder="smtp.gmail.com" data-testid="smtp-host-input" required />
                      </div>
                      <div className="space-y-2">
                        <Label>Port</Label>
                        <Input type="number" value={smtpForm.port} onChange={e => setSmtpForm({...smtpForm, port: e.target.value})} placeholder="587" data-testid="smtp-port-input" required />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Username</Label>
                        <Input value={smtpForm.username} onChange={e => setSmtpForm({...smtpForm, username: e.target.value})} placeholder="your@email.com" data-testid="smtp-username-input" required />
                      </div>
                      <div className="space-y-2">
                        <Label>Password {smtpConfig?.has_password && <span className="text-xs text-[hsl(var(--ff-success))]">(saved)</span>}</Label>
                        <Input type="password" value={smtpForm.password} onChange={e => setSmtpForm({...smtpForm, password: e.target.value})} placeholder={smtpConfig?.has_password ? "••••••••" : "Enter password"} data-testid="smtp-password-input" required={!smtpConfig} />
                        {smtpConfig?.has_password && <p className="text-xs text-muted-foreground">Leave empty to keep existing password</p>}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>From Email</Label>
                        <Input type="email" value={smtpForm.from_email} onChange={e => setSmtpForm({...smtpForm, from_email: e.target.value})} placeholder="noreply@yourdomain.com" data-testid="smtp-from-email-input" required />
                      </div>
                      <div className="space-y-2">
                        <Label>From Name</Label>
                        <Input value={smtpForm.from_name} onChange={e => setSmtpForm({...smtpForm, from_name: e.target.value})} placeholder="FinFlow" data-testid="smtp-from-name-input" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Brand Name <span className="text-muted-foreground text-xs">(used in email subject)</span></Label>
                      <Input value={smtpForm.brand_name} onChange={e => setSmtpForm({...smtpForm, brand_name: e.target.value})} placeholder="FinFlow" data-testid="smtp-brand-name-input" />
                      <p className="text-xs text-muted-foreground">This name will appear in email subjects: "Your [Brand Name] Verification Code"</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="use_tls">Use TLS/STARTTLS</Label>
                      <Switch id="use_tls" checked={smtpForm.use_tls} onCheckedChange={checked => setSmtpForm({...smtpForm, use_tls: checked})} data-testid="smtp-tls-switch" />
                    </div>
                    <Button type="submit" className="w-full" disabled={isSavingSmtp} data-testid="smtp-save-button">
                      {isSavingSmtp ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save Configuration
                    </Button>
                    {smtpConfig && (
                      <div className="pt-4 border-t">
                        <Label className="text-sm font-medium">Send Test Email</Label>
                        <div className="flex gap-2 mt-2">
                          <Input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="test@example.com" data-testid="smtp-test-email-input" />
                          <Button type="button" variant="outline" onClick={handleTestSmtp} disabled={!testEmail || isTesting} data-testid="smtp-test-button">
                            {isTesting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}Test
                          </Button>
                        </div>
                      </div>
                    )}
                  </form>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Languages Tab */}
          <TabsContent value="languages">
            <Card data-testid="admin-languages-panel">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Languages Management</CardTitle>
                  <CardDescription>Manage available languages and translations</CardDescription>
                </div>
                <Button 
                  onClick={handleSeedTranslations} 
                  disabled={isSeedingTranslations}
                  data-testid="seed-translations-button"
                >
                  {isSeedingTranslations ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Seed Translations
                </Button>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="space-y-4">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Languages List */}
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Code</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Native Name</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Default</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {languages.map(lang => (
                          <TableRow key={lang.code}>
                            <TableCell className="font-mono">{lang.code}</TableCell>
                            <TableCell>{lang.name}</TableCell>
                            <TableCell>{lang.native_name}</TableCell>
                            <TableCell>
                              <Badge className={lang.is_enabled ? "bg-[hsl(var(--ff-success)/.15)] text-[hsl(var(--ff-success))]" : "bg-muted text-muted-foreground"}>
                                {lang.is_enabled ? "Enabled" : "Disabled"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {lang.is_default && <Badge variant="outline">Default</Badge>}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => {
                                    setSelectedLangForEdit(lang);
                                    fetchTranslationsForLanguage(lang.code);
                                  }}
                                  data-testid={`edit-translations-${lang.code}`}
                                >
                                  <Edit className="h-4 w-4 mr-1" /> Translations
                                </Button>
                                <Switch
                                  checked={lang.is_enabled}
                                  onCheckedChange={(checked) => handleToggleLanguage(lang.code, checked)}
                                  disabled={lang.is_default}
                                  data-testid={`toggle-language-${lang.code}`}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Agent Configuration Tab */}
          <TabsContent value="ai">
            <div className="space-y-6">
              {/* API Keys Configuration */}
              <Card data-testid="ai-api-keys-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="h-5 w-5" />
                    API Keys Configuration
                  </CardTitle>
                  <CardDescription>Configure your OpenRouter, Tavily, and Perplexity API keys for AI functionality</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* OpenRouter Keys */}
                  <div className="space-y-4">
                    <h4 className="font-medium flex items-center gap-2">
                      OpenRouter
                      {aiConfig?.has_openrouter_key && <Badge variant="outline" className="text-green-600 border-green-600"><Check className="h-3 w-3 mr-1" /> Configured</Badge>}
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>API Key</Label>
                        <div className="relative">
                          <Input
                            type={showApiKeys.openrouter ? "text" : "password"}
                            placeholder={aiConfig?.has_openrouter_key ? "••••••••••••" : "sk-or-v1-..."}
                            value={aiConfigForm.openrouter_api_key}
                            onChange={(e) => setAiConfigForm(prev => ({ ...prev, openrouter_api_key: e.target.value }))}
                            data-testid="openrouter-api-key-input"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6"
                            onClick={() => setShowApiKeys(prev => ({ ...prev, openrouter: !prev.openrouter }))}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Provisioning Key (Optional)</Label>
                        <div className="relative">
                          <Input
                            type={showApiKeys.provisioning ? "text" : "password"}
                            placeholder={aiConfig?.has_provisioning_key ? "••••••••••••" : "sk-or-prov-..."}
                            value={aiConfigForm.openrouter_provisioning_key}
                            onChange={(e) => setAiConfigForm(prev => ({ ...prev, openrouter_provisioning_key: e.target.value }))}
                            data-testid="openrouter-provisioning-key-input"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6"
                            onClick={() => setShowApiKeys(prev => ({ ...prev, provisioning: !prev.provisioning }))}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={handleValidateApiKey} disabled={!aiConfig?.has_openrouter_key} data-testid="validate-openrouter-button">
                        <Check className="h-4 w-4 mr-2" /> Validate Connection
                      </Button>
                    </div>
                  </div>

                  {/* Web Search Keys */}
                  <div className="space-y-4 pt-4 border-t">
                    <h4 className="font-medium">Web Search APIs</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          Tavily API Key
                          {aiConfig?.has_tavily_key && <Badge variant="outline" className="text-green-600 border-green-600 text-xs"><Check className="h-3 w-3" /></Badge>}
                        </Label>
                        <div className="relative">
                          <Input
                            type={showApiKeys.tavily ? "text" : "password"}
                            placeholder={aiConfig?.has_tavily_key ? "••••••••••••" : "tvly-..."}
                            value={aiConfigForm.tavily_api_key}
                            onChange={(e) => setAiConfigForm(prev => ({ ...prev, tavily_api_key: e.target.value }))}
                            data-testid="tavily-api-key-input"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6"
                            onClick={() => setShowApiKeys(prev => ({ ...prev, tavily: !prev.tavily }))}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          Perplexity API Key
                          {aiConfig?.has_perplexity_key && <Badge variant="outline" className="text-green-600 border-green-600 text-xs"><Check className="h-3 w-3" /></Badge>}
                        </Label>
                        <div className="relative">
                          <Input
                            type={showApiKeys.perplexity ? "text" : "password"}
                            placeholder={aiConfig?.has_perplexity_key ? "••••••••••••" : "pplx-..."}
                            value={aiConfigForm.perplexity_api_key}
                            onChange={(e) => setAiConfigForm(prev => ({ ...prev, perplexity_api_key: e.target.value }))}
                            data-testid="perplexity-api-key-input"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6"
                            onClick={() => setShowApiKeys(prev => ({ ...prev, perplexity: !prev.perplexity }))}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button onClick={handleSaveAiConfig} disabled={isSavingAiConfig} data-testid="save-ai-config-button">
                      {isSavingAiConfig ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Save API Keys
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Model Selection */}
              <Card data-testid="ai-models-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bot className="h-5 w-5" />
                    Model Selection
                  </CardTitle>
                  <CardDescription>Select default and fallback models from OpenRouter. Models are fetched in real-time.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!aiConfig?.has_openrouter_key ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Please configure OpenRouter API key first to view available models.</p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-4">
                        <div className="relative flex-1">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Search models by name or ID..."
                            value={modelSearchQuery}
                            onChange={(e) => setModelSearchQuery(e.target.value)}
                            className="pl-9"
                            data-testid="model-search-input"
                          />
                        </div>
                        <Button variant="outline" onClick={handleRefreshModels} disabled={isLoadingAiModels} data-testid="refresh-models-button">
                          <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingAiModels ? "animate-spin" : ""}`} />
                          Refresh Models
                        </Button>
                      </div>

                      {/* Current Selection Summary - More Prominent */}
                      <div className="p-4 bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg border border-primary/20">
                        <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                          <Settings className="h-4 w-4" />
                          Current Model Configuration
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Default Model</Label>
                            {aiConfig?.default_model_id ? (
                              <div className="flex items-center gap-2">
                                <Badge variant="default" className="font-mono text-xs">
                                  {aiConfig.default_model_id.split('/')[1] || aiConfig.default_model_id}
                                </Badge>
                                <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => handleSetDefaultModel("")}>
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground">Not selected</Badge>
                            )}
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Fallback Models ({aiConfig?.fallback_model_ids?.length || 0}/5)</Label>
                            <div className="flex flex-wrap gap-1">
                              {aiConfig?.fallback_model_ids?.length > 0 ? (
                                aiConfig.fallback_model_ids.map((id, idx) => (
                                  <Badge key={id} variant="secondary" className="font-mono text-xs flex items-center gap-1">
                                    {idx + 1}. {id.split('/')[1] || id}
                                    <X className="h-3 w-3 cursor-pointer hover:text-destructive" onClick={() => handleToggleFallbackModel(id)} />
                                  </Badge>
                                ))
                              ) : (
                                <Badge variant="outline" className="text-muted-foreground">None</Badge>
                              )}
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">PDF Processing Model</Label>
                            {aiConfig?.pdf_processing_model_id ? (
                              <div className="flex items-center gap-2">
                                <Badge variant="default" className="font-mono text-xs bg-amber-600">
                                  {aiConfig.pdf_processing_model_id.split('/')[1] || aiConfig.pdf_processing_model_id}
                                </Badge>
                                <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => handleSetPdfModel("")}>
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground">Not selected</Badge>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Models Table */}
                      {isLoadingAiModels ? (
                        <div className="space-y-2">
                          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                        </div>
                      ) : aiModels.length === 0 ? (
                        <div className="text-center py-8">
                          <Button onClick={fetchAiModels} data-testid="fetch-models-button">
                            <RefreshCw className="h-4 w-4 mr-2" /> Load Models from OpenRouter
                          </Button>
                        </div>
                      ) : (
                        <div className="max-h-96 overflow-auto border rounded-lg">
                          <Table>
                            <TableHeader className="sticky top-0 bg-background">
                              <TableRow>
                                <TableHead className="w-[250px]">Model</TableHead>
                                <TableHead>Context</TableHead>
                                <TableHead>Price ($/1M)</TableHead>
                                <TableHead className="text-center">Default</TableHead>
                                <TableHead className="text-center">Fallback</TableHead>
                                <TableHead className="text-center">PDF</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {getFilteredModels().slice(0, 50).map(model => {
                                const isDefault = model.id === aiConfig?.default_model_id;
                                const isFallback = aiConfig?.fallback_model_ids?.includes(model.id);
                                const isPdf = model.id === aiConfig?.pdf_processing_model_id;
                                const isSelected = isDefault || isFallback || isPdf;
                                
                                return (
                                <TableRow 
                                  key={model.id} 
                                  className={`
                                    ${isDefault ? "bg-primary/10 border-l-4 border-l-primary" : ""}
                                    ${isFallback && !isDefault ? "bg-secondary/50 border-l-4 border-l-secondary" : ""}
                                    ${isPdf && !isDefault && !isFallback ? "bg-amber-500/10 border-l-4 border-l-amber-500" : ""}
                                  `}
                                >
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <div>
                                        <p className="font-medium text-sm">{model.name}</p>
                                        <p className="text-xs text-muted-foreground font-mono">{model.id}</p>
                                      </div>
                                      {isDefault && <Badge variant="default" className="text-xs">Default</Badge>}
                                      {isFallback && <Badge variant="secondary" className="text-xs">Fallback #{aiConfig.fallback_model_ids.indexOf(model.id) + 1}</Badge>}
                                      {isPdf && <Badge className="text-xs bg-amber-600">PDF</Badge>}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-sm">{model.context_length?.toLocaleString()}</TableCell>
                                  <TableCell className="text-sm font-mono">
                                    ${model.pricing?.prompt?.toFixed(2) || "0.00"}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <Button
                                      variant={model.id === aiConfig?.default_model_id ? "default" : "outline"}
                                      size="sm"
                                      onClick={() => handleSetDefaultModel(model.id)}
                                      data-testid={`set-default-${model.id}`}
                                    >
                                      {model.id === aiConfig?.default_model_id ? <Check className="h-4 w-4" /> : "Set"}
                                    </Button>
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <Button
                                      variant={aiConfig?.fallback_model_ids?.includes(model.id) ? "default" : "outline"}
                                      size="sm"
                                      onClick={() => handleToggleFallbackModel(model.id)}
                                      disabled={model.id === aiConfig?.default_model_id}
                                      data-testid={`toggle-fallback-${model.id}`}
                                    >
                                      {aiConfig?.fallback_model_ids?.includes(model.id) ? <Check className="h-4 w-4" /> : "+"}
                                    </Button>
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <Button
                                      variant={model.id === aiConfig?.pdf_processing_model_id ? "default" : "outline"}
                                      size="sm"
                                      onClick={() => handleSetPdfModel(model.id)}
                                      data-testid={`set-pdf-${model.id}`}
                                    >
                                      {model.id === aiConfig?.pdf_processing_model_id ? <Check className="h-4 w-4" /> : "Set"}
                                    </Button>
                                  </TableCell>
                                </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* AI System Prompt */}
              <Card data-testid="ai-system-prompt-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    AI System Prompt
                  </CardTitle>
                  <CardDescription>Customize the AI's persona, name, and behavior separately for Individual (Personal) and Business users.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Account Type Tabs */}
                  <Tabs value={aiAccountTypeTab} onValueChange={setAiAccountTypeTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="individual" className="flex items-center gap-2" data-testid="ai-tab-individual">
                        <User className="h-4 w-4" />
                        Individual Users
                      </TabsTrigger>
                      <TabsTrigger value="business" className="flex items-center gap-2" data-testid="ai-tab-business">
                        <Building className="h-4 w-4" />
                        Business Users
                      </TabsTrigger>
                    </TabsList>
                    
                    {/* Individual Users Tab */}
                    <TabsContent value="individual" className="space-y-4 mt-4">
                      <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                        <p className="text-xs text-blue-800 dark:text-blue-200">
                          <strong>Individual Users:</strong> Personal finance management - income, expenses, categories, savings goals, budget tracking.
                        </p>
                      </div>
                      
                      {/* Editable Default Prompt - Individual */}
                      <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="default-prompt-individual" className="border rounded-lg px-4">
                          <AccordionTrigger className="hover:no-underline">
                            <div className="flex items-center gap-2 text-sm">
                              <Edit className="h-4 w-4" />
                              Edit Default System Prompt (Individual)
                              {aiConfigForm.individual_default_system_prompt && (
                                <Badge variant="outline" className="ml-2 text-xs">Modified</Badge>
                              )}
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="space-y-3">
                              <Textarea
                                value={aiConfigForm.individual_default_system_prompt || individualFactoryDefaultPrompt}
                                onChange={(e) => setAiConfigForm(prev => ({ ...prev, individual_default_system_prompt: e.target.value }))}
                                className="min-h-[400px] font-mono text-xs"
                                placeholder="Enter your default system prompt for individual users..."
                                data-testid="individual-default-system-prompt-textarea"
                              />
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Badge variant={aiConfigForm.individual_default_system_prompt ? "default" : "secondary"} className="text-xs">
                                  {aiConfigForm.individual_default_system_prompt ? "Custom Default Active" : "Using Factory Default"}
                                </Badge>
                                {aiConfigForm.individual_default_system_prompt && !aiConfigForm.individual_default_system_prompt.includes("{data_field_descriptions}") && (
                                  <Badge variant="destructive" className="text-xs">
                                    Missing {"{data_field_descriptions}"} placeholder
                                  </Badge>
                                )}
                              </div>
                              <div className="flex justify-between items-center pt-2 border-t">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setAiConfigForm(prev => ({ ...prev, individual_default_system_prompt: "" }))}
                                  disabled={!aiConfigForm.individual_default_system_prompt || isSavingDefaultPrompt}
                                  data-testid="individual-reset-to-factory-btn"
                                >
                                  <RotateCcw className="h-3 w-3 mr-1" />
                                  Reset to Factory Default
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      setIsSavingDefaultPrompt(true);
                                      await api.put("/admin/ai/config", { individual_default_system_prompt: aiConfigForm.individual_default_system_prompt });
                                      toast.success("Individual default prompt saved");
                                      const res = await api.get("/admin/ai/config");
                                      setAiConfig(res.data);
                                    } catch (error) {
                                      toast.error("Failed to save default prompt");
                                    } finally {
                                      setIsSavingDefaultPrompt(false);
                                    }
                                  }}
                                  disabled={isSavingDefaultPrompt}
                                  data-testid="individual-save-default-prompt-btn"
                                >
                                  {isSavingDefaultPrompt ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                                  {isSavingDefaultPrompt ? "Saving..." : "Save Default Prompt"}
                                </Button>
                              </div>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>

                      {/* View Factory Default - Individual */}
                      <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="factory-default-individual" className="border border-dashed rounded-lg px-4">
                          <AccordionTrigger className="hover:no-underline">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Eye className="h-4 w-4" />
                              View Factory Default (Individual)
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="bg-muted/50 rounded-lg p-4 text-xs font-mono whitespace-pre-wrap max-h-[300px] overflow-y-auto">
                              {individualFactoryDefaultPrompt || "Loading factory default..."}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>

                      {/* Custom Override - Individual */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Custom Override Prompt (Individual)</Label>
                          <Badge variant="outline" className="text-xs">Highest Priority</Badge>
                        </div>
                        <Textarea
                          value={aiConfigForm.individual_custom_system_prompt}
                          onChange={(e) => setAiConfigForm(prev => ({ ...prev, individual_custom_system_prompt: e.target.value }))}
                          placeholder="Leave blank to use the default prompt. Enter a complete custom prompt to override."
                          className="min-h-[150px] font-mono text-sm"
                          data-testid="individual-custom-system-prompt-textarea"
                        />
                        <div className="flex justify-between items-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setAiConfigForm(prev => ({ ...prev, individual_custom_system_prompt: "" }))}
                            disabled={!aiConfigForm.individual_custom_system_prompt}
                          >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Clear Override
                          </Button>
                          <Button
                            size="sm"
                            onClick={async () => {
                              try {
                                setIsSavingAiConfig(true);
                                await api.put("/admin/ai/config", { individual_custom_system_prompt: aiConfigForm.individual_custom_system_prompt });
                                toast.success("Individual custom override saved");
                                const res = await api.get("/admin/ai/config");
                                setAiConfig(res.data);
                              } catch (error) {
                                toast.error("Failed to save custom override");
                              } finally {
                                setIsSavingAiConfig(false);
                              }
                            }}
                            disabled={isSavingAiConfig}
                          >
                            {isSavingAiConfig ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                            Save Override
                          </Button>
                        </div>
                      </div>
                    </TabsContent>
                    
                    {/* Business Users Tab */}
                    <TabsContent value="business" className="space-y-4 mt-4">
                      <div className="bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
                        <p className="text-xs text-purple-800 dark:text-purple-200">
                          <strong>Business Users:</strong> Company finance management - revenue, sales, clients, products/services, invoices, recurring revenue, financial objectives.
                        </p>
                      </div>
                      
                      {/* Editable Default Prompt - Business */}
                      <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="default-prompt-business" className="border rounded-lg px-4">
                          <AccordionTrigger className="hover:no-underline">
                            <div className="flex items-center gap-2 text-sm">
                              <Edit className="h-4 w-4" />
                              Edit Default System Prompt (Business)
                              {aiConfigForm.business_default_system_prompt && (
                                <Badge variant="outline" className="ml-2 text-xs">Modified</Badge>
                              )}
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="space-y-3">
                              <Textarea
                                value={aiConfigForm.business_default_system_prompt || businessFactoryDefaultPrompt}
                                onChange={(e) => setAiConfigForm(prev => ({ ...prev, business_default_system_prompt: e.target.value }))}
                                className="min-h-[400px] font-mono text-xs"
                                placeholder="Enter your default system prompt for business users..."
                                data-testid="business-default-system-prompt-textarea"
                              />
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Badge variant={aiConfigForm.business_default_system_prompt ? "default" : "secondary"} className="text-xs">
                                  {aiConfigForm.business_default_system_prompt ? "Custom Default Active" : "Using Factory Default"}
                                </Badge>
                                {aiConfigForm.business_default_system_prompt && !aiConfigForm.business_default_system_prompt.includes("{data_field_descriptions}") && (
                                  <Badge variant="destructive" className="text-xs">
                                    Missing {"{data_field_descriptions}"} placeholder
                                  </Badge>
                                )}
                              </div>
                              <div className="flex justify-between items-center pt-2 border-t">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setAiConfigForm(prev => ({ ...prev, business_default_system_prompt: "" }))}
                                  disabled={!aiConfigForm.business_default_system_prompt || isSavingDefaultPrompt}
                                  data-testid="business-reset-to-factory-btn"
                                >
                                  <RotateCcw className="h-3 w-3 mr-1" />
                                  Reset to Factory Default
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      setIsSavingDefaultPrompt(true);
                                      await api.put("/admin/ai/config", { business_default_system_prompt: aiConfigForm.business_default_system_prompt });
                                      toast.success("Business default prompt saved");
                                      const res = await api.get("/admin/ai/config");
                                      setAiConfig(res.data);
                                    } catch (error) {
                                      toast.error("Failed to save default prompt");
                                    } finally {
                                      setIsSavingDefaultPrompt(false);
                                    }
                                  }}
                                  disabled={isSavingDefaultPrompt}
                                  data-testid="business-save-default-prompt-btn"
                                >
                                  {isSavingDefaultPrompt ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                                  {isSavingDefaultPrompt ? "Saving..." : "Save Default Prompt"}
                                </Button>
                              </div>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>

                      {/* View Factory Default - Business */}
                      <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="factory-default-business" className="border border-dashed rounded-lg px-4">
                          <AccordionTrigger className="hover:no-underline">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Eye className="h-4 w-4" />
                              View Factory Default (Business)
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="bg-muted/50 rounded-lg p-4 text-xs font-mono whitespace-pre-wrap max-h-[300px] overflow-y-auto">
                              {businessFactoryDefaultPrompt || "Loading factory default..."}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>

                      {/* Custom Override - Business */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Custom Override Prompt (Business)</Label>
                          <Badge variant="outline" className="text-xs">Highest Priority</Badge>
                        </div>
                        <Textarea
                          value={aiConfigForm.business_custom_system_prompt}
                          onChange={(e) => setAiConfigForm(prev => ({ ...prev, business_custom_system_prompt: e.target.value }))}
                          placeholder="Leave blank to use the default prompt. Enter a complete custom prompt to override."
                          className="min-h-[150px] font-mono text-sm"
                          data-testid="business-custom-system-prompt-textarea"
                        />
                        <div className="flex justify-between items-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setAiConfigForm(prev => ({ ...prev, business_custom_system_prompt: "" }))}
                            disabled={!aiConfigForm.business_custom_system_prompt}
                          >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Clear Override
                          </Button>
                          <Button
                            size="sm"
                            onClick={async () => {
                              try {
                                setIsSavingAiConfig(true);
                                await api.put("/admin/ai/config", { business_custom_system_prompt: aiConfigForm.business_custom_system_prompt });
                                toast.success("Business custom override saved");
                                const res = await api.get("/admin/ai/config");
                                setAiConfig(res.data);
                              } catch (error) {
                                toast.error("Failed to save custom override");
                              } finally {
                                setIsSavingAiConfig(false);
                              }
                            }}
                            disabled={isSavingAiConfig}
                          >
                            {isSavingAiConfig ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                            Save Override
                          </Button>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>

                  {/* Available Variables Documentation */}
                  <div className="bg-slate-50 dark:bg-slate-950/30 border border-slate-200 dark:border-slate-800 rounded-lg p-4 mt-6">
                    <h4 className="font-medium text-sm flex items-center gap-2 mb-2">
                      <Settings className="h-4 w-4" />
                      Available Placeholders (Both Account Types)
                    </h4>
                    <div className="space-y-2 text-xs">
                      <div className="flex items-start gap-2">
                        <code className="bg-slate-100 dark:bg-slate-900/50 px-2 py-1 rounded shrink-0">{"{data_field_descriptions}"}</code>
                        <span className="text-muted-foreground">Injects admin-configured data field descriptions. Different fields are injected for Individual vs Business users.</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3 pt-2 border-t border-slate-200 dark:border-slate-800">
                      <strong>Note:</strong> Individual users get goals_data, Business users get company_data (products, clients, sales, objectives).
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Platform Help Guide */}
              <Card data-testid="ai-platform-help-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="h-5 w-5" />
                    Platform Help Guide
                  </CardTitle>
                  <CardDescription>Define instructions that help the AI guide users on how to use the FinFlow platform, separately for Individual and Business users.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Account Type Tabs for Help Guide */}
                  <Tabs value={aiAccountTypeTab} onValueChange={setAiAccountTypeTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="individual" className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Individual Users
                      </TabsTrigger>
                      <TabsTrigger value="business" className="flex items-center gap-2">
                        <Building className="h-4 w-4" />
                        Business Users
                      </TabsTrigger>
                    </TabsList>
                    
                    {/* Individual Help Guide Tab */}
                    <TabsContent value="individual" className="space-y-4 mt-4">
                      {/* Enable/Disable Toggle - Individual */}
                      <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                        <div className="space-y-0.5">
                          <Label className="text-base">Enable Help Guide (Individual)</Label>
                          <p className="text-sm text-muted-foreground">When enabled, the AI can answer "how to" questions for individual users</p>
                        </div>
                        <Switch
                          checked={aiConfigForm.individual_platform_help_enabled}
                          onCheckedChange={(checked) => setAiConfigForm(prev => ({ ...prev, individual_platform_help_enabled: checked }))}
                          data-testid="individual-platform-help-enabled-toggle"
                        />
                      </div>

                      {/* Help Guide Content - Individual */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Help Guide Content (Individual)</Label>
                          <Badge variant={aiConfigForm.individual_platform_help_guide ? "default" : "secondary"} className="text-xs">
                            {aiConfigForm.individual_platform_help_guide ? "Custom Guide Active" : "Using Default Guide"}
                          </Badge>
                        </div>
                        <Textarea
                          value={aiConfigForm.individual_platform_help_guide || individualDefaultHelpGuide}
                          onChange={(e) => setAiConfigForm(prev => ({ ...prev, individual_platform_help_guide: e.target.value }))}
                          className="min-h-[300px] font-mono text-sm"
                          placeholder="Enter platform usage instructions for individual users..."
                          disabled={!aiConfigForm.individual_platform_help_enabled}
                          data-testid="individual-platform-help-guide-textarea"
                        />
                      </div>

                      {/* View Default - Individual */}
                      <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="default-help-individual" className="border border-dashed rounded-lg px-4">
                          <AccordionTrigger className="hover:no-underline">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Eye className="h-4 w-4" />
                              View Default Help Guide (Individual)
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="bg-muted/50 rounded-lg p-4 text-xs font-mono whitespace-pre-wrap max-h-[300px] overflow-y-auto">
                              {individualDefaultHelpGuide || "Loading default help guide..."}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>

                      {/* Action Buttons - Individual */}
                      <div className="flex justify-between items-center pt-2 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAiConfigForm(prev => ({ ...prev, individual_platform_help_guide: "" }))}
                          disabled={!aiConfigForm.individual_platform_help_guide || isSavingHelpGuide}
                        >
                          <RotateCcw className="h-3 w-3 mr-1" />
                          Reset to Default
                        </Button>
                        <Button
                          size="sm"
                          onClick={async () => {
                            try {
                              setIsSavingHelpGuide(true);
                              await api.put("/admin/ai/config", { 
                                individual_platform_help_guide: aiConfigForm.individual_platform_help_guide,
                                individual_platform_help_enabled: aiConfigForm.individual_platform_help_enabled
                              });
                              toast.success("Individual help guide saved");
                              const res = await api.get("/admin/ai/config");
                              setAiConfig(res.data);
                            } catch (error) {
                              toast.error("Failed to save help guide");
                            } finally {
                              setIsSavingHelpGuide(false);
                            }
                          }}
                          disabled={isSavingHelpGuide}
                        >
                          {isSavingHelpGuide ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                          Save Help Guide
                        </Button>
                      </div>
                    </TabsContent>
                    
                    {/* Business Help Guide Tab */}
                    <TabsContent value="business" className="space-y-4 mt-4">
                      {/* Enable/Disable Toggle - Business */}
                      <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
                        <div className="space-y-0.5">
                          <Label className="text-base">Enable Help Guide (Business)</Label>
                          <p className="text-sm text-muted-foreground">When enabled, the AI can answer "how to" questions for business users</p>
                        </div>
                        <Switch
                          checked={aiConfigForm.business_platform_help_enabled}
                          onCheckedChange={(checked) => setAiConfigForm(prev => ({ ...prev, business_platform_help_enabled: checked }))}
                          data-testid="business-platform-help-enabled-toggle"
                        />
                      </div>

                      {/* Help Guide Content - Business */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Help Guide Content (Business)</Label>
                          <Badge variant={aiConfigForm.business_platform_help_guide ? "default" : "secondary"} className="text-xs">
                            {aiConfigForm.business_platform_help_guide ? "Custom Guide Active" : "Using Default Guide"}
                          </Badge>
                        </div>
                        <Textarea
                          value={aiConfigForm.business_platform_help_guide || businessDefaultHelpGuide}
                          onChange={(e) => setAiConfigForm(prev => ({ ...prev, business_platform_help_guide: e.target.value }))}
                          className="min-h-[300px] font-mono text-sm"
                          placeholder="Enter platform usage instructions for business users..."
                          disabled={!aiConfigForm.business_platform_help_enabled}
                          data-testid="business-platform-help-guide-textarea"
                        />
                      </div>

                      {/* View Default - Business */}
                      <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="default-help-business" className="border border-dashed rounded-lg px-4">
                          <AccordionTrigger className="hover:no-underline">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Eye className="h-4 w-4" />
                              View Default Help Guide (Business)
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="bg-muted/50 rounded-lg p-4 text-xs font-mono whitespace-pre-wrap max-h-[300px] overflow-y-auto">
                              {businessDefaultHelpGuide || "Loading default help guide..."}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>

                      {/* Action Buttons - Business */}
                      <div className="flex justify-between items-center pt-2 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAiConfigForm(prev => ({ ...prev, business_platform_help_guide: "" }))}
                          disabled={!aiConfigForm.business_platform_help_guide || isSavingHelpGuide}
                        >
                          <RotateCcw className="h-3 w-3 mr-1" />
                          Reset to Default
                        </Button>
                        <Button
                          size="sm"
                          onClick={async () => {
                            try {
                              setIsSavingHelpGuide(true);
                              await api.put("/admin/ai/config", { 
                                business_platform_help_guide: aiConfigForm.business_platform_help_guide,
                                business_platform_help_enabled: aiConfigForm.business_platform_help_enabled
                              });
                              toast.success("Business help guide saved");
                              const res = await api.get("/admin/ai/config");
                              setAiConfig(res.data);
                            } catch (error) {
                              toast.error("Failed to save help guide");
                            } finally {
                              setIsSavingHelpGuide(false);
                            }
                          }}
                          disabled={isSavingHelpGuide}
                        >
                          {isSavingHelpGuide ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                          Save Help Guide
                        </Button>
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>

              {/* Language Configuration */}
              <Card data-testid="ai-language-config-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Language Configuration
                  </CardTitle>
                  <CardDescription>Configure which languages the AI can respond in. Languages are dynamically loaded from Languages Management.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Available Languages from Languages Management */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-base">Enabled AI Languages</Label>
                      <Badge variant="outline" className="text-xs">
                        {availableLanguages.length} languages available
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground -mt-1">
                      Select which languages the AI can respond in. The AI will respond in the user's preferred language setting.
                    </p>
                    
                    {availableLanguages.length > 0 ? (
                      <div className="grid gap-2 border rounded-lg p-4">
                        {availableLanguages.map((lang) => (
                          <div key={lang.code} className="flex items-center justify-between p-2 rounded hover:bg-muted/50">
                            <div className="flex items-center gap-3">
                              <Checkbox
                                id={`lang-${lang.code}`}
                                checked={aiConfigForm.enabled_ai_languages.includes(lang.code)}
                                onCheckedChange={(checked) => {
                                  setAiConfigForm(prev => ({
                                    ...prev,
                                    enabled_ai_languages: checked
                                      ? [...prev.enabled_ai_languages, lang.code]
                                      : prev.enabled_ai_languages.filter(l => l !== lang.code)
                                  }));
                                }}
                                data-testid={`lang-checkbox-${lang.code}`}
                              />
                              <label htmlFor={`lang-${lang.code}`} className="cursor-pointer">
                                <span className="font-medium">{lang.name}</span>
                                {lang.native_name !== lang.name && (
                                  <span className="text-muted-foreground ml-2">({lang.native_name})</span>
                                )}
                              </label>
                            </div>
                            <Badge variant={aiConfigForm.enabled_ai_languages.includes(lang.code) ? "default" : "secondary"} className="text-xs">
                              {lang.code}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center p-6 border rounded-lg text-muted-foreground">
                        <Globe className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No languages configured.</p>
                        <p className="text-sm">Add languages in the Languages Management tab.</p>
                      </div>
                    )}
                    
                    {aiConfigForm.enabled_ai_languages.length === 0 && (
                      <p className="text-sm text-destructive">At least one language must be enabled.</p>
                    )}
                  </div>

                  {/* Use Platform Translations Toggle */}
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div className="space-y-0.5">
                      <Label className="text-base">Use Platform Translations</Label>
                      <p className="text-sm text-muted-foreground">
                        When enabled, the AI will use correct UI terminology from Languages Management (e.g., "Panel de Control" for Dashboard in Spanish)
                      </p>
                    </div>
                    <Switch
                      checked={aiConfigForm.use_platform_translations}
                      onCheckedChange={(checked) => setAiConfigForm(prev => ({ ...prev, use_platform_translations: checked }))}
                      data-testid="use-platform-translations-toggle"
                    />
                  </div>

                  {/* Info Box */}
                  <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <h4 className="font-medium text-sm flex items-center gap-2 mb-2 text-blue-800 dark:text-blue-200">
                      <Settings className="h-4 w-4" />
                      How It Works
                    </h4>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• The AI reads the user's language preference from their Settings</li>
                      <li>• If the user's language is enabled above, the AI responds in that language</li>
                      <li>• UI translations are injected so the AI uses correct terminology</li>
                      <li>• New languages added in Languages Management will automatically appear here</li>
                    </ul>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end pt-2 border-t">
                    <Button
                      onClick={async () => {
                        if (aiConfigForm.enabled_ai_languages.length === 0) {
                          toast.error("At least one language must be enabled");
                          return;
                        }
                        try {
                          setIsSavingLanguageConfig(true);
                          await api.put("/admin/ai/config", {
                            enabled_ai_languages: aiConfigForm.enabled_ai_languages,
                            use_platform_translations: aiConfigForm.use_platform_translations
                          });
                          toast.success("Language configuration saved successfully");
                          // Refresh AI config
                          const res = await api.get("/admin/ai/config");
                          setAiConfig(res.data);
                        } catch (error) {
                          toast.error("Failed to save language configuration");
                        } finally {
                          setIsSavingLanguageConfig(false);
                        }
                      }}
                      disabled={isSavingLanguageConfig || aiConfigForm.enabled_ai_languages.length === 0}
                      data-testid="save-language-config-btn"
                    >
                      {isSavingLanguageConfig ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                      {isSavingLanguageConfig ? "Saving..." : "Save Language Config"}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Thinking Layer Configuration */}
              <Card data-testid="ai-thinking-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="h-5 w-5" />
                    Thinking Layer Configuration
                  </CardTitle>
                  <CardDescription>Configure how the AI processes queries, ranks sources, and generates responses</CardDescription>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="intent">
                      <AccordionTrigger>Intent Classification</AccordionTrigger>
                      <AccordionContent className="space-y-4 pt-4">
                        <div className="space-y-2">
                          <Label>Model (leave empty for default)</Label>
                          <Input
                            placeholder="e.g., openai/gpt-4o-mini"
                            value={thinkingForm.intent_classification.model_id || ""}
                            onChange={(e) => setThinkingForm(prev => ({
                              ...prev,
                              intent_classification: { ...prev.intent_classification, model_id: e.target.value }
                            }))}
                            data-testid="intent-model-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Prompt Template</Label>
                          <Textarea
                            rows={8}
                            value={thinkingForm.intent_classification.prompt_template || ""}
                            onChange={(e) => setThinkingForm(prev => ({
                              ...prev,
                              intent_classification: { ...prev.intent_classification, prompt_template: e.target.value }
                            }))}
                            data-testid="intent-prompt-input"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={thinkingForm.intent_classification.show_to_user}
                            onCheckedChange={(checked) => setThinkingForm(prev => ({
                              ...prev,
                              intent_classification: { ...prev.intent_classification, show_to_user: checked }
                            }))}
                            data-testid="intent-show-user-switch"
                          />
                          <Label>Show to user</Label>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="ranking">
                      <AccordionTrigger>Source Ranking</AccordionTrigger>
                      <AccordionContent className="space-y-4 pt-4">
                        <div className="space-y-2">
                          <Label>Model (leave empty for default)</Label>
                          <Input
                            placeholder="e.g., openai/gpt-4o-mini"
                            value={thinkingForm.source_ranking.model_id || ""}
                            onChange={(e) => setThinkingForm(prev => ({
                              ...prev,
                              source_ranking: { ...prev.source_ranking, model_id: e.target.value }
                            }))}
                            data-testid="ranking-model-input"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label>User Data Weight: {thinkingForm.source_ranking.user_data_weight}%</Label>
                            <Slider
                              value={[thinkingForm.source_ranking.user_data_weight]}
                              onValueChange={([value]) => setThinkingForm(prev => ({
                                ...prev,
                                source_ranking: { ...prev.source_ranking, user_data_weight: value }
                              }))}
                              max={100}
                              step={5}
                              data-testid="user-data-weight-slider"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Admin KB Weight: {thinkingForm.source_ranking.admin_kb_weight}%</Label>
                            <Slider
                              value={[thinkingForm.source_ranking.admin_kb_weight]}
                              onValueChange={([value]) => setThinkingForm(prev => ({
                                ...prev,
                                source_ranking: { ...prev.source_ranking, admin_kb_weight: value }
                              }))}
                              max={100}
                              step={5}
                              data-testid="admin-kb-weight-slider"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Web Search Weight: {thinkingForm.source_ranking.web_search_weight}%</Label>
                            <Slider
                              value={[thinkingForm.source_ranking.web_search_weight]}
                              onValueChange={([value]) => setThinkingForm(prev => ({
                                ...prev,
                                source_ranking: { ...prev.source_ranking, web_search_weight: value }
                              }))}
                              max={100}
                              step={5}
                              data-testid="web-search-weight-slider"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Max Sources to Include: {thinkingForm.source_ranking.max_sources}</Label>
                          <Slider
                            value={[thinkingForm.source_ranking.max_sources]}
                            onValueChange={([value]) => setThinkingForm(prev => ({
                              ...prev,
                              source_ranking: { ...prev.source_ranking, max_sources: value }
                            }))}
                            min={1}
                            max={20}
                            step={1}
                            data-testid="max-sources-slider"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={thinkingForm.source_ranking.show_to_user}
                            onCheckedChange={(checked) => setThinkingForm(prev => ({
                              ...prev,
                              source_ranking: { ...prev.source_ranking, show_to_user: checked }
                            }))}
                            data-testid="ranking-show-user-switch"
                          />
                          <Label>Show to user</Label>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="response">
                      <AccordionTrigger>Response Generation</AccordionTrigger>
                      <AccordionContent className="space-y-4 pt-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Response Style</Label>
                            <Select
                              value={thinkingForm.response_generation.style}
                              onValueChange={(value) => setThinkingForm(prev => ({
                                ...prev,
                                response_generation: { ...prev.response_generation, style: value }
                              }))}
                            >
                              <SelectTrigger data-testid="response-style-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="professional_friendly">Professional & Friendly</SelectItem>
                                <SelectItem value="concise">Concise</SelectItem>
                                <SelectItem value="detailed">Detailed</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Max Tokens: {thinkingForm.response_generation.max_tokens}</Label>
                            <Slider
                              value={[thinkingForm.response_generation.max_tokens]}
                              onValueChange={([value]) => setThinkingForm(prev => ({
                                ...prev,
                                response_generation: { ...prev.response_generation, max_tokens: value }
                              }))}
                              min={500}
                              max={8000}
                              step={100}
                              data-testid="max-tokens-slider"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Temperature: {thinkingForm.response_generation.temperature}</Label>
                          <Slider
                            value={[thinkingForm.response_generation.temperature * 100]}
                            onValueChange={([value]) => setThinkingForm(prev => ({
                              ...prev,
                              response_generation: { ...prev.response_generation, temperature: value / 100 }
                            }))}
                            min={0}
                            max={200}
                            step={5}
                            data-testid="temperature-slider"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={thinkingForm.response_generation.show_sources}
                            onCheckedChange={(checked) => setThinkingForm(prev => ({
                              ...prev,
                              response_generation: { ...prev.response_generation, show_sources: checked }
                            }))}
                            data-testid="show-sources-switch"
                          />
                          <Label>Show sources in response</Label>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="web_search">
                      <AccordionTrigger>
                        <div className="flex items-center gap-2">
                          <Search className="h-4 w-4" />
                          Web Search Configuration
                          {thinkingForm.web_search?.enabled && <Badge variant="outline" className="ml-2 text-xs">Enabled</Badge>}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 pt-4">
                        <div className="p-4 bg-muted/50 rounded-lg">
                          <p className="text-sm text-muted-foreground">
                            Enable web search to allow the AI to fetch real-time information from the internet when the knowledge base doesn't have relevant data.
                            Requires either Tavily or Perplexity API key to be configured.
                          </p>
                        </div>

                        <div className="flex items-center justify-between p-4 border rounded-lg">
                          <div>
                            <Label className="font-medium">Enable Web Search</Label>
                            <p className="text-xs text-muted-foreground mt-1">Allow AI to search the web for information</p>
                          </div>
                          <Switch
                            checked={thinkingForm.web_search?.enabled || false}
                            onCheckedChange={(checked) => setThinkingForm(prev => ({
                              ...prev,
                              web_search: { ...prev.web_search, enabled: checked }
                            }))}
                            disabled={!aiConfig?.has_tavily_key && !aiConfig?.has_perplexity_key}
                            data-testid="web-search-enabled-switch"
                          />
                        </div>

                        {(!aiConfig?.has_tavily_key && !aiConfig?.has_perplexity_key) && (
                          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                            <p className="text-sm text-amber-600 dark:text-amber-400">
                              <AlertCircle className="h-4 w-4 inline mr-2" />
                              Please configure Tavily or Perplexity API key in the API Keys section above to enable web search.
                            </p>
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Search Provider</Label>
                            <Select
                              value={thinkingForm.web_search?.provider || "tavily"}
                              onValueChange={(value) => setThinkingForm(prev => ({
                                ...prev,
                                web_search: { ...prev.web_search, provider: value }
                              }))}
                              disabled={!thinkingForm.web_search?.enabled}
                            >
                              <SelectTrigger data-testid="web-search-provider-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="tavily" disabled={!aiConfig?.has_tavily_key}>
                                  Tavily {!aiConfig?.has_tavily_key && "(No API Key)"}
                                </SelectItem>
                                <SelectItem value="perplexity" disabled={!aiConfig?.has_perplexity_key}>
                                  Perplexity {!aiConfig?.has_perplexity_key && "(No API Key)"}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Max Results: {thinkingForm.web_search?.max_results || 3}</Label>
                            <Slider
                              value={[thinkingForm.web_search?.max_results || 3]}
                              onValueChange={([value]) => setThinkingForm(prev => ({
                                ...prev,
                                web_search: { ...prev.web_search, max_results: value }
                              }))}
                              min={1}
                              max={10}
                              step={1}
                              disabled={!thinkingForm.web_search?.enabled}
                              data-testid="web-search-max-results-slider"
                            />
                          </div>
                        </div>

                        <div className="flex items-center justify-between p-4 border rounded-lg">
                          <div>
                            <Label className="font-medium">Auto Search</Label>
                            <p className="text-xs text-muted-foreground mt-1">Automatically search when knowledge base lacks relevant info</p>
                          </div>
                          <Switch
                            checked={thinkingForm.web_search?.auto_search || false}
                            onCheckedChange={(checked) => setThinkingForm(prev => ({
                              ...prev,
                              web_search: { ...prev.web_search, auto_search: checked }
                            }))}
                            disabled={!thinkingForm.web_search?.enabled}
                            data-testid="web-search-auto-switch"
                          />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  <div className="flex justify-end pt-4 border-t mt-4">
                    <Button onClick={handleSaveThinking} disabled={isSavingThinking} data-testid="save-thinking-button">
                      {isSavingThinking ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Save Thinking Layer Config
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Guardrails Configuration */}
              <Card data-testid="ai-guardrails-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Guardrails Configuration
                  </CardTitle>
                  <CardDescription>Configure safety guardrails and custom restrictions for the AI assistant</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Built-in Guardrails */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={guardrailsForm.prevent_off_topic}
                            onCheckedChange={(checked) => setGuardrailsForm(prev => ({ ...prev, prevent_off_topic: checked }))}
                            data-testid="off-topic-switch"
                          />
                          <Label className="font-medium">Prevent Off-Topic Conversations</Label>
                        </div>
                        <Input
                          className="mt-2"
                          placeholder="Message to show when blocked..."
                          value={guardrailsForm.off_topic_message}
                          onChange={(e) => setGuardrailsForm(prev => ({ ...prev, off_topic_message: e.target.value }))}
                          disabled={!guardrailsForm.prevent_off_topic}
                          data-testid="off-topic-message-input"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={guardrailsForm.block_harmful_content}
                            onCheckedChange={(checked) => setGuardrailsForm(prev => ({ ...prev, block_harmful_content: checked }))}
                            data-testid="harmful-content-switch"
                          />
                          <Label className="font-medium">Block Harmful/Inappropriate Content</Label>
                        </div>
                        <Input
                          className="mt-2"
                          placeholder="Message to show when blocked..."
                          value={guardrailsForm.harmful_content_message}
                          onChange={(e) => setGuardrailsForm(prev => ({ ...prev, harmful_content_message: e.target.value }))}
                          disabled={!guardrailsForm.block_harmful_content}
                          data-testid="harmful-content-message-input"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={guardrailsForm.restrict_sensitive_data}
                            onCheckedChange={(checked) => setGuardrailsForm(prev => ({ ...prev, restrict_sensitive_data: checked }))}
                            data-testid="sensitive-data-switch"
                          />
                          <Label className="font-medium">Restrict Sensitive Financial Data</Label>
                        </div>
                        <Input
                          className="mt-2"
                          placeholder="Message to show when blocked..."
                          value={guardrailsForm.sensitive_data_message}
                          onChange={(e) => setGuardrailsForm(prev => ({ ...prev, sensitive_data_message: e.target.value }))}
                          disabled={!guardrailsForm.restrict_sensitive_data}
                          data-testid="sensitive-data-message-input"
                        />
                      </div>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="font-medium">Response Length Limit</Label>
                        <div className="flex items-center gap-2">
                          <Label htmlFor="disable-length-limit" className="text-sm text-muted-foreground">Disable Limit</Label>
                          <Switch 
                            id="disable-length-limit"
                            checked={guardrailsForm.disable_response_length_limit}
                            onCheckedChange={(checked) => setGuardrailsForm(prev => ({ ...prev, disable_response_length_limit: checked }))}
                            data-testid="disable-length-limit-switch"
                          />
                        </div>
                      </div>
                      {!guardrailsForm.disable_response_length_limit && (
                        <>
                          <div className="flex items-center gap-4 mt-2">
                            <Slider
                              value={[guardrailsForm.max_response_length]}
                              onValueChange={([value]) => setGuardrailsForm(prev => ({ ...prev, max_response_length: value }))}
                              min={500}
                              max={8000}
                              step={100}
                              className="flex-1"
                              data-testid="response-length-slider"
                            />
                            <span className="text-sm font-mono w-20">{guardrailsForm.max_response_length}</span>
                          </div>
                          <Input
                            className="mt-2"
                            placeholder="Message when truncated..."
                            value={guardrailsForm.length_exceeded_message}
                            onChange={(e) => setGuardrailsForm(prev => ({ ...prev, length_exceeded_message: e.target.value }))}
                            data-testid="length-exceeded-message-input"
                          />
                        </>
                      )}
                      {guardrailsForm.disable_response_length_limit && (
                        <p className="text-sm text-muted-foreground mt-2">Response length limit is disabled. AI responses will not be truncated.</p>
                      )}
                    </div>
                  </div>

                  {/* Custom Restrictions */}
                  <div className="space-y-4 pt-4 border-t">
                    <h4 className="font-medium">Custom Restrictions (Regex Patterns)</h4>
                    
                    {guardrailsForm.custom_restrictions.length > 0 && (
                      <div className="space-y-2">
                        {guardrailsForm.custom_restrictions.map((restriction) => (
                          <div key={restriction.id} className="flex items-center gap-2 p-2 border rounded">
                            <code className="flex-1 text-xs bg-muted px-2 py-1 rounded">{restriction.pattern}</code>
                            <span className="flex-1 text-sm truncate">{restriction.message}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteRestriction(restriction.id)}
                              data-testid={`delete-restriction-${restriction.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Input
                        placeholder="Regex pattern (e.g., .*password.*)"
                        value={newRestriction.pattern}
                        onChange={(e) => setNewRestriction(prev => ({ ...prev, pattern: e.target.value }))}
                        className="flex-1"
                        data-testid="new-restriction-pattern-input"
                      />
                      <Input
                        placeholder="Message to show"
                        value={newRestriction.message}
                        onChange={(e) => setNewRestriction(prev => ({ ...prev, message: e.target.value }))}
                        className="flex-1"
                        data-testid="new-restriction-message-input"
                      />
                      <Button variant="outline" onClick={handleAddRestriction} data-testid="add-restriction-button">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4 border-t">
                    <Button onClick={handleSaveGuardrails} disabled={isSavingGuardrails} data-testid="save-guardrails-button">
                      {isSavingGuardrails ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Save Guardrails Config
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Knowledge Base Management */}
              <Card data-testid="ai-kb-card">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Server className="h-5 w-5" />
                      Knowledge Base
                    </span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={fetchKbData} disabled={isLoadingKb} data-testid="refresh-kb-button">
                        <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingKb ? "animate-spin" : ""}`} />
                        Refresh
                      </Button>
                      <Button size="sm" onClick={() => setShowAddDocDialog(true)} data-testid="add-doc-button">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Document
                      </Button>
                      <label>
                        <input type="file" accept=".pdf" onChange={handleUploadPdf} className="hidden" disabled={isUploadingDoc} data-testid="upload-pdf-input" />
                        <Button variant="outline" size="sm" asChild>
                          <span className="cursor-pointer">
                            {isUploadingDoc ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                            Upload PDF
                          </span>
                        </Button>
                      </label>
                    </div>
                  </CardTitle>
                  <CardDescription>Manage the AI agent's knowledge base documents (RAG data source)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* KB Stats */}
                  <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                    <div className="text-center">
                      <p className="text-2xl font-bold">{kbStats.document_count}</p>
                      <p className="text-sm text-muted-foreground">Documents</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold">{kbStats.chunk_count}</p>
                      <p className="text-sm text-muted-foreground">Chunks</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold">{kbStats.total_size_mb} MB</p>
                      <p className="text-sm text-muted-foreground">Total Size</p>
                    </div>
                  </div>

                  {/* Documents List */}
                  {isLoadingKb ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                    </div>
                  ) : kbDocuments.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Server className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>No documents in knowledge base</p>
                      <p className="text-sm">Add text documents or upload PDFs to get started</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Title</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Chunks</TableHead>
                            <TableHead>Tags</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {kbDocuments.map(doc => (
                            <TableRow key={doc.id}>
                              <TableCell className="font-medium">{doc.title}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{doc.type.toUpperCase()}</Badge>
                              </TableCell>
                              <TableCell>{doc.chunk_count}</TableCell>
                              <TableCell>
                                <div className="flex gap-1 flex-wrap">
                                  {doc.tags?.slice(0, 3).map(tag => (
                                    <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {format(parseISO(doc.created_at), "MMM d, yyyy")}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex gap-1 justify-end">
                                  <Button variant="ghost" size="icon" onClick={() => handleViewChunks(doc.id)} data-testid={`view-chunks-${doc.id}`}>
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="icon" onClick={() => handleDeleteDocument(doc.id)} data-testid={`delete-doc-${doc.id}`}>
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {/* Chunks Viewer */}
                  {selectedDocForChunks && (
                    <div className="border rounded-lg p-4 mt-4">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium">Document Chunks ({docChunks.length})</h4>
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedDocForChunks(null); setDocChunks([]); }}>
                          Close
                        </Button>
                      </div>
                      <div className="max-h-64 overflow-auto space-y-2">
                        {docChunks.map((chunk, i) => (
                          <div key={chunk.id} className="p-3 bg-muted/30 rounded text-sm">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs">Chunk {i + 1}</Badge>
                              <span className="text-xs text-muted-foreground">{chunk.token_count} tokens</span>
                            </div>
                            <p className="line-clamp-3">{chunk.content}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* AI Data Sources Configuration */}
              <Card data-testid="ai-data-sources-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Sliders className="h-5 w-5" />
                        AI Data Sources Configuration
                      </CardTitle>
                      <CardDescription>Configure which user data the AI can access and provide descriptions to guide AI behavior</CardDescription>
                    </div>
                    <Button onClick={handleSaveDataSources} disabled={isSavingDataSources} data-testid="save-data-sources-button">
                      {isSavingDataSources ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                      Save Configuration
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Dashboard Data */}
                  <div className="p-4 border rounded-lg space-y-4">
                    <h4 className="font-medium flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Dashboard Data
                    </h4>
                    <div className="space-y-4">
                      {/* Monthly Summary */}
                      <div className="p-3 bg-muted/30 rounded-lg space-y-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="font-medium text-sm">Monthly Summary</Label>
                            <p className="text-xs text-muted-foreground">Income, expense, balance totals</p>
                          </div>
                          <Switch
                            checked={dataSourcesForm.dashboard_data?.monthly_summary ?? true}
                            onCheckedChange={(checked) => setDataSourcesForm(prev => ({
                              ...prev,
                              dashboard_data: { ...prev.dashboard_data, monthly_summary: checked }
                            }))}
                            data-testid="ds-monthly-summary"
                          />
                        </div>
                        {dataSourcesForm.dashboard_data?.monthly_summary && (
                          <Textarea
                            placeholder="Describe how the AI should interpret and use this data..."
                            value={dataSourcesForm.dashboard_data?.monthly_summary_description || ""}
                            onChange={(e) => setDataSourcesForm(prev => ({
                              ...prev,
                              dashboard_data: { ...prev.dashboard_data, monthly_summary_description: e.target.value }
                            }))}
                            className="text-sm min-h-[60px]"
                            data-testid="ds-monthly-summary-desc"
                          />
                        )}
                      </div>
                      
                      {/* Payment Status */}
                      <div className="p-3 bg-muted/30 rounded-lg space-y-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="font-medium text-sm">Payment Status</Label>
                            <p className="text-xs text-muted-foreground">Paid/pending transaction counts</p>
                          </div>
                          <Switch
                            checked={dataSourcesForm.dashboard_data?.payment_status ?? true}
                            onCheckedChange={(checked) => setDataSourcesForm(prev => ({
                              ...prev,
                              dashboard_data: { ...prev.dashboard_data, payment_status: checked }
                            }))}
                            data-testid="ds-payment-status"
                          />
                        </div>
                        {dataSourcesForm.dashboard_data?.payment_status && (
                          <Textarea
                            placeholder="Describe how the AI should interpret and use this data..."
                            value={dataSourcesForm.dashboard_data?.payment_status_description || ""}
                            onChange={(e) => setDataSourcesForm(prev => ({
                              ...prev,
                              dashboard_data: { ...prev.dashboard_data, payment_status_description: e.target.value }
                            }))}
                            className="text-sm min-h-[60px]"
                            data-testid="ds-payment-status-desc"
                          />
                        )}
                      </div>
                      
                      {/* Current Month Context */}
                      <div className="p-3 bg-muted/30 rounded-lg space-y-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="font-medium text-sm">Current Month Context</Label>
                            <p className="text-xs text-muted-foreground">Which month the user is viewing</p>
                          </div>
                          <Switch
                            checked={dataSourcesForm.dashboard_data?.current_month_context ?? true}
                            onCheckedChange={(checked) => setDataSourcesForm(prev => ({
                              ...prev,
                              dashboard_data: { ...prev.dashboard_data, current_month_context: checked }
                            }))}
                            data-testid="ds-month-context"
                          />
                        </div>
                        {dataSourcesForm.dashboard_data?.current_month_context && (
                          <Textarea
                            placeholder="Describe how the AI should interpret and use this data..."
                            value={dataSourcesForm.dashboard_data?.current_month_context_description || ""}
                            onChange={(e) => setDataSourcesForm(prev => ({
                              ...prev,
                              dashboard_data: { ...prev.dashboard_data, current_month_context_description: e.target.value }
                            }))}
                            className="text-sm min-h-[60px]"
                            data-testid="ds-month-context-desc"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Transaction Data */}
                  <div className="p-4 border rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium flex items-center gap-2">
                        <Wallet className="h-4 w-4" />
                        Transaction Data
                      </h4>
                      <Switch
                        checked={dataSourcesForm.transaction_data?.enabled ?? true}
                        onCheckedChange={(checked) => setDataSourcesForm(prev => ({
                          ...prev,
                          transaction_data: { ...prev.transaction_data, enabled: checked }
                        }))}
                        data-testid="ds-transactions-enabled"
                      />
                    </div>
                    {dataSourcesForm.transaction_data?.enabled && (
                      <div className="space-y-3">
                        <Textarea
                          placeholder="General description for transaction data..."
                          value={dataSourcesForm.transaction_data?.enabled_description || ""}
                          onChange={(e) => setDataSourcesForm(prev => ({
                            ...prev,
                            transaction_data: { ...prev.transaction_data, enabled_description: e.target.value }
                          }))}
                          className="text-sm min-h-[60px]"
                          data-testid="ds-tx-enabled-desc"
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {[
                            { key: "include_date", label: "Date", desc: "Transaction date" },
                            { key: "include_name", label: "Name", desc: "Transaction name/title" },
                            { key: "include_type", label: "Type", desc: "Income or Expense" },
                            { key: "include_category_names", label: "Category", desc: "Human-readable category names" },
                            { key: "include_original_amount", label: "Original Amount", desc: "Amount in original currency" },
                            { key: "include_converted_amount", label: "Converted Amount", desc: "Amount in base currency" },
                            { key: "include_balance", label: "Balance", desc: "Running balance" },
                            { key: "include_status", label: "Status", desc: "Confirmed, Upcoming, Skipped" },
                            { key: "include_payment_status", label: "Payment Status", desc: "Paid or Unpaid" },
                            { key: "include_recurring", label: "Recurring", desc: "Monthly, Weekly, Yearly" },
                            { key: "include_next_due", label: "Next Due", desc: "Next due date" },
                            { key: "include_descriptions", label: "Description", desc: "Transaction notes" },
                            { key: "include_search_info", label: "Search Functionality", desc: "Search bar capabilities" }
                          ].map(item => (
                            <div key={item.key} className="p-3 bg-muted/20 rounded-lg space-y-2">
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={dataSourcesForm.transaction_data?.[item.key] ?? false}
                                  onCheckedChange={(checked) => setDataSourcesForm(prev => ({
                                    ...prev,
                                    transaction_data: { ...prev.transaction_data, [item.key]: checked }
                                  }))}
                                  data-testid={`ds-tx-${item.key}`}
                                />
                                <div>
                                  <Label className="text-sm font-medium">{item.label}</Label>
                                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                                </div>
                              </div>
                              {dataSourcesForm.transaction_data?.[item.key] && (
                                <Textarea
                                  placeholder={`Describe ${item.label.toLowerCase()} for AI...`}
                                  value={dataSourcesForm.transaction_data?.[`${item.key}_description`] || ""}
                                  onChange={(e) => setDataSourcesForm(prev => ({
                                    ...prev,
                                    transaction_data: { ...prev.transaction_data, [`${item.key}_description`]: e.target.value }
                                  }))}
                                  className="text-xs min-h-[50px]"
                                  data-testid={`ds-tx-${item.key}-desc`}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Categories & Goals Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Categories Data */}
                    <div className="p-4 border rounded-lg space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium flex items-center gap-2">
                          <Package className="h-4 w-4" />
                          Categories Data
                        </h4>
                        <Switch
                          checked={dataSourcesForm.categories_data?.enabled ?? true}
                          onCheckedChange={(checked) => setDataSourcesForm(prev => ({
                            ...prev,
                            categories_data: { ...prev.categories_data, enabled: checked }
                          }))}
                          data-testid="ds-categories-enabled"
                        />
                      </div>
                      {dataSourcesForm.categories_data?.enabled && (
                        <div className="space-y-3">
                          <Textarea
                            placeholder="General description for categories data..."
                            value={dataSourcesForm.categories_data?.enabled_description || ""}
                            onChange={(e) => setDataSourcesForm(prev => ({
                              ...prev,
                              categories_data: { ...prev.categories_data, enabled_description: e.target.value }
                            }))}
                            className="text-sm min-h-[50px]"
                            data-testid="ds-categories-desc"
                          />
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 p-2 bg-muted/20 rounded">
                              <Switch
                                checked={dataSourcesForm.categories_data?.include_types ?? true}
                                onCheckedChange={(checked) => setDataSourcesForm(prev => ({
                                  ...prev,
                                  categories_data: { ...prev.categories_data, include_types: checked }
                                }))}
                              />
                              <Label className="text-sm">Include Types (income/expense)</Label>
                            </div>
                            {dataSourcesForm.categories_data?.include_types && (
                              <Textarea
                                placeholder="Describe category types for AI..."
                                value={dataSourcesForm.categories_data?.include_types_description || ""}
                                onChange={(e) => setDataSourcesForm(prev => ({
                                  ...prev,
                                  categories_data: { ...prev.categories_data, include_types_description: e.target.value }
                                }))}
                                className="text-xs min-h-[40px] ml-6"
                                data-testid="ds-categories-types-desc"
                              />
                            )}
                            <div className="flex items-center gap-2 p-2 bg-muted/20 rounded">
                              <Switch
                                checked={dataSourcesForm.categories_data?.include_icons_colors ?? false}
                                onCheckedChange={(checked) => setDataSourcesForm(prev => ({
                                  ...prev,
                                  categories_data: { ...prev.categories_data, include_icons_colors: checked }
                                }))}
                              />
                              <Label className="text-sm">Include Icons & Colors</Label>
                            </div>
                            {dataSourcesForm.categories_data?.include_icons_colors && (
                              <Textarea
                                placeholder="Describe icons/colors for AI..."
                                value={dataSourcesForm.categories_data?.include_icons_colors_description || ""}
                                onChange={(e) => setDataSourcesForm(prev => ({
                                  ...prev,
                                  categories_data: { ...prev.categories_data, include_icons_colors_description: e.target.value }
                                }))}
                                className="text-xs min-h-[40px] ml-6"
                                data-testid="ds-categories-icons-desc"
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Goals Data */}
                    <div className="p-4 border rounded-lg space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium flex items-center gap-2">
                          <Target className="h-4 w-4" />
                          Goals Data
                        </h4>
                        <Switch
                          checked={dataSourcesForm.goals_data?.enabled ?? true}
                          onCheckedChange={(checked) => setDataSourcesForm(prev => ({
                            ...prev,
                            goals_data: { ...prev.goals_data, enabled: checked }
                          }))}
                          data-testid="ds-goals-enabled"
                        />
                      </div>
                      {dataSourcesForm.goals_data?.enabled && (
                        <div className="space-y-3">
                          <Textarea
                            placeholder="General description for goals data..."
                            value={dataSourcesForm.goals_data?.enabled_description || ""}
                            onChange={(e) => setDataSourcesForm(prev => ({
                              ...prev,
                              goals_data: { ...prev.goals_data, enabled_description: e.target.value }
                            }))}
                            className="text-sm min-h-[50px]"
                            data-testid="ds-goals-desc"
                          />
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 p-2 bg-muted/20 rounded">
                              <Switch
                                checked={dataSourcesForm.goals_data?.include_progress ?? true}
                                onCheckedChange={(checked) => setDataSourcesForm(prev => ({
                                  ...prev,
                                  goals_data: { ...prev.goals_data, include_progress: checked }
                                }))}
                              />
                              <Label className="text-sm">Include Progress (target/current)</Label>
                            </div>
                            {dataSourcesForm.goals_data?.include_progress && (
                              <Textarea
                                placeholder="Describe goal progress for AI..."
                                value={dataSourcesForm.goals_data?.include_progress_description || ""}
                                onChange={(e) => setDataSourcesForm(prev => ({
                                  ...prev,
                                  goals_data: { ...prev.goals_data, include_progress_description: e.target.value }
                                }))}
                                className="text-xs min-h-[40px] ml-6"
                                data-testid="ds-goals-progress-desc"
                              />
                            )}
                            <div className="flex items-center gap-2 p-2 bg-muted/20 rounded">
                              <Switch
                                checked={dataSourcesForm.goals_data?.include_deadlines ?? true}
                                onCheckedChange={(checked) => setDataSourcesForm(prev => ({
                                  ...prev,
                                  goals_data: { ...prev.goals_data, include_deadlines: checked }
                                }))}
                              />
                              <Label className="text-sm">Include Deadlines</Label>
                            </div>
                            {dataSourcesForm.goals_data?.include_deadlines && (
                              <Textarea
                                placeholder="Describe deadlines for AI..."
                                value={dataSourcesForm.goals_data?.include_deadlines_description || ""}
                                onChange={(e) => setDataSourcesForm(prev => ({
                                  ...prev,
                                  goals_data: { ...prev.goals_data, include_deadlines_description: e.target.value }
                                }))}
                                className="text-xs min-h-[40px] ml-6"
                                data-testid="ds-goals-deadlines-desc"
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* User Memory */}
                  <div className="p-4 border rounded-lg space-y-4">
                    <h4 className="font-medium flex items-center gap-2">
                      <Brain className="h-4 w-4" />
                      User Memory (AI Memory Settings)
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-3 bg-muted/20 rounded-lg space-y-2">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={dataSourcesForm.user_memory?.include_memories ?? true}
                            onCheckedChange={(checked) => setDataSourcesForm(prev => ({
                              ...prev,
                              user_memory: { ...prev.user_memory, include_memories: checked }
                            }))}
                            data-testid="ds-include-memories"
                          />
                          <Label className="text-sm font-medium">Include Personal Memories</Label>
                        </div>
                        {dataSourcesForm.user_memory?.include_memories && (
                          <Textarea
                            placeholder="Describe personal memories for AI..."
                            value={dataSourcesForm.user_memory?.include_memories_description || ""}
                            onChange={(e) => setDataSourcesForm(prev => ({
                              ...prev,
                              user_memory: { ...prev.user_memory, include_memories_description: e.target.value }
                            }))}
                            className="text-xs min-h-[50px]"
                            data-testid="ds-memories-desc"
                          />
                        )}
                      </div>
                      <div className="p-3 bg-muted/20 rounded-lg space-y-2">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={dataSourcesForm.user_memory?.include_objectives ?? true}
                            onCheckedChange={(checked) => setDataSourcesForm(prev => ({
                              ...prev,
                              user_memory: { ...prev.user_memory, include_objectives: checked }
                            }))}
                            data-testid="ds-include-objectives"
                          />
                          <Label className="text-sm font-medium">Include Financial Objectives</Label>
                        </div>
                        {dataSourcesForm.user_memory?.include_objectives && (
                          <Textarea
                            placeholder="Describe financial objectives for AI..."
                            value={dataSourcesForm.user_memory?.include_objectives_description || ""}
                            onChange={(e) => setDataSourcesForm(prev => ({
                              ...prev,
                              user_memory: { ...prev.user_memory, include_objectives_description: e.target.value }
                            }))}
                            className="text-xs min-h-[50px]"
                            data-testid="ds-objectives-desc"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Data Filters */}
                  <div className="p-4 border rounded-lg space-y-4">
                    <h4 className="font-medium flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Data Filters
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-3 bg-muted/20 rounded-lg space-y-2">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={dataSourcesForm.filters?.exclude_deleted ?? true}
                            onCheckedChange={(checked) => setDataSourcesForm(prev => ({
                              ...prev,
                              filters: { ...prev.filters, exclude_deleted: checked }
                            }))}
                            data-testid="ds-exclude-deleted"
                          />
                          <Label className="text-sm">Exclude Deleted Transactions</Label>
                        </div>
                        <Textarea
                          placeholder="Describe filter for AI..."
                          value={dataSourcesForm.filters?.exclude_deleted_description || ""}
                          onChange={(e) => setDataSourcesForm(prev => ({
                            ...prev,
                            filters: { ...prev.filters, exclude_deleted_description: e.target.value }
                          }))}
                          className="text-xs min-h-[40px]"
                          data-testid="ds-exclude-deleted-desc"
                        />
                      </div>
                      <div className="p-3 bg-muted/20 rounded-lg space-y-2">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={dataSourcesForm.filters?.exclude_skipped ?? true}
                            onCheckedChange={(checked) => setDataSourcesForm(prev => ({
                              ...prev,
                              filters: { ...prev.filters, exclude_skipped: checked }
                            }))}
                            data-testid="ds-exclude-skipped"
                          />
                          <Label className="text-sm">Exclude Skipped Transactions</Label>
                        </div>
                        <Textarea
                          placeholder="Describe filter for AI..."
                          value={dataSourcesForm.filters?.exclude_skipped_description || ""}
                          onChange={(e) => setDataSourcesForm(prev => ({
                            ...prev,
                            filters: { ...prev.filters, exclude_skipped_description: e.target.value }
                          }))}
                          className="text-xs min-h-[40px]"
                          data-testid="ds-exclude-skipped-desc"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Default Time Range</Label>
                        <Select
                          value={dataSourcesForm.filters?.default_time_range || "current_month"}
                          onValueChange={(value) => setDataSourcesForm(prev => ({
                            ...prev,
                            filters: { ...prev.filters, default_time_range: value }
                          }))}
                        >
                          <SelectTrigger data-testid="ds-time-range">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="current_month">Current Month</SelectItem>
                            <SelectItem value="last_3_months">Last 3 Months</SelectItem>
                            <SelectItem value="all_time">All Time</SelectItem>
                          </SelectContent>
                        </Select>
                        <Textarea
                          placeholder="Describe time range for AI..."
                          value={dataSourcesForm.filters?.default_time_range_description || ""}
                          onChange={(e) => setDataSourcesForm(prev => ({
                            ...prev,
                            filters: { ...prev.filters, default_time_range_description: e.target.value }
                          }))}
                          className="text-xs min-h-[40px]"
                          data-testid="ds-time-range-desc"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Company Data (Business Users Only) */}
                  <div className="p-4 border rounded-lg space-y-4 border-blue-500/30 bg-blue-50/30 dark:bg-blue-950/10">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium flex items-center gap-2">
                          <Building className="h-4 w-4 text-blue-600" />
                          Company Data
                          <Badge variant="outline" className="text-xs ml-2">Business Users Only</Badge>
                        </h4>
                        <p className="text-xs text-muted-foreground mt-1">Configure data access for company/business user accounts</p>
                      </div>
                      <Switch
                        checked={dataSourcesForm.company_data?.enabled ?? true}
                        onCheckedChange={(checked) => setDataSourcesForm(prev => ({
                          ...prev,
                          company_data: { ...prev.company_data, enabled: checked }
                        }))}
                        data-testid="ds-company-enabled"
                      />
                    </div>
                    {dataSourcesForm.company_data?.enabled && (
                      <div className="space-y-3">
                        <Textarea
                          placeholder="General description for company data access..."
                          value={dataSourcesForm.company_data?.enabled_description || ""}
                          onChange={(e) => setDataSourcesForm(prev => ({
                            ...prev,
                            company_data: { ...prev.company_data, enabled_description: e.target.value }
                          }))}
                          className="text-sm min-h-[50px]"
                          data-testid="ds-company-desc"
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {[
                            { key: "products_services", label: "Products & Services", desc: "Product catalog and pricing", icon: "📦" },
                            { key: "clients", label: "Clients", desc: "Client database and revenue", icon: "👥" },
                            { key: "sales_records", label: "Sales Records", desc: "Sales transactions and history", icon: "💰" },
                            { key: "financial_objectives", label: "Financial Objectives", desc: "Business targets and goals", icon: "🎯" },
                            { key: "sales_analytics", label: "Sales Analytics", desc: "Revenue trends and metrics", icon: "📊" },
                            { key: "company_profile", label: "Company Profile", desc: "Business name, industry, size", icon: "🏢" }
                          ].map(item => (
                            <div key={item.key} className="p-3 bg-muted/20 rounded-lg space-y-2">
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={dataSourcesForm.company_data?.[item.key] ?? true}
                                  onCheckedChange={(checked) => setDataSourcesForm(prev => ({
                                    ...prev,
                                    company_data: { ...prev.company_data, [item.key]: checked }
                                  }))}
                                  data-testid={`ds-company-${item.key}`}
                                />
                                <div>
                                  <Label className="text-sm font-medium">{item.icon} {item.label}</Label>
                                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                                </div>
                              </div>
                              {dataSourcesForm.company_data?.[item.key] && (
                                <Textarea
                                  placeholder={`Describe ${item.label.toLowerCase()} for AI...`}
                                  value={dataSourcesForm.company_data?.[`${item.key}_description`] || ""}
                                  onChange={(e) => setDataSourcesForm(prev => ({
                                    ...prev,
                                    company_data: { ...prev.company_data, [`${item.key}_description`]: e.target.value }
                                  }))}
                                  className="text-xs min-h-[50px]"
                                  data-testid={`ds-company-${item.key}-desc`}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* AI Query Audit Log */}
              <Card data-testid="ai-queries-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <History className="h-5 w-5" />
                        AI Query Audit Log
                      </CardTitle>
                      <CardDescription>Review user queries and AI responses</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchAiQueries} disabled={isLoadingQueries} data-testid="refresh-queries-button">
                      <RefreshCw className={`h-4 w-4 mr-1 ${isLoadingQueries ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Query Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-3 rounded-lg border bg-muted/30">
                      <p className="text-xs text-muted-foreground">This Month</p>
                      <p className="text-xl font-bold">{aiQueryStats.month_queries}</p>
                    </div>
                    <div className="p-3 rounded-lg border bg-muted/30">
                      <p className="text-xs text-muted-foreground">This Week</p>
                      <p className="text-xl font-bold">{aiQueryStats.week_queries}</p>
                    </div>
                    <div className="p-3 rounded-lg border bg-muted/30">
                      <p className="text-xs text-muted-foreground">Today</p>
                      <p className="text-xl font-bold">{aiQueryStats.today_queries}</p>
                    </div>
                    <div className="p-3 rounded-lg border bg-muted/30">
                      <p className="text-xs text-muted-foreground">Tokens (Month)</p>
                      <p className="text-xl font-bold">{aiQueryStats.total_tokens_this_month?.toLocaleString() || 0}</p>
                    </div>
                  </div>

                  {/* Blocked Queries Warning */}
                  {aiQueryStats.blocked_queries > 0 && (
                    <div className="flex items-center gap-2 p-3 rounded-lg border border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
                      <AlertCircle className="h-4 w-4 text-amber-500" />
                      <span className="text-sm">{aiQueryStats.blocked_queries} queries blocked by guardrails this month</span>
                    </div>
                  )}

                  {/* Query List */}
                  {isLoadingQueries ? (
                    <div className="space-y-2">
                      <Skeleton className="h-12 w-full" />
                      <Skeleton className="h-12 w-full" />
                      <Skeleton className="h-12 w-full" />
                    </div>
                  ) : aiQueries.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <MessageSquare className="h-10 w-10 mx-auto mb-2 opacity-50" />
                      <p>No AI queries yet</p>
                      <p className="text-xs mt-1">User queries will appear here once the AI chat is used</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>User</TableHead>
                            <TableHead>Query</TableHead>
                            <TableHead>Tokens</TableHead>
                            <TableHead>Time</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {aiQueries.slice(0, 10).map((q) => (
                            <TableRow key={q.id} data-testid={`query-row-${q.id}`}>
                              <TableCell className="text-xs">{q.user_email || q.user_id?.slice(0, 8)}</TableCell>
                              <TableCell className="max-w-[200px] truncate text-sm">{q.query}</TableCell>
                              <TableCell className="text-sm">{q.total_tokens_used}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {q.created_at ? format(parseISO(q.created_at), "MMM d, h:mm a") : '-'}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleViewQueryDetail(q.id)}
                                  data-testid={`view-query-${q.id}`}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Company Pages Visibility (Admin-Controlled) */}
              <Card className="border-blue-200 dark:border-blue-800" data-testid="company-pages-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building className="h-5 w-5 text-blue-600" />
                    Company Pages Visibility
                    <Badge variant="outline" className="ml-2 text-blue-600 border-blue-600">
                      Admin Control
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    Enable or disable specific pages for all company users. Disabled pages will not appear on any company user's dashboard.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Group by category */}
                    {['Reporting & Analytics', 'Finance', 'Business Intelligence', 'Industry (Product)', 'Industry (Service)', 'Project Management'].map(category => {
                      const categoryPages = COMPANY_PAGES.filter(p => p.category === category);
                      if (categoryPages.length === 0) return null;
                      
                      return (
                        <div key={category}>
                          <h4 className="text-sm font-medium text-muted-foreground mb-3">{category}</h4>
                          <div className="space-y-2">
                            {categoryPages.map(page => {
                              const PageIcon = page.icon;
                              const isEnabled = !disabledCompanyPages.includes(page.id);
                              
                              return (
                                <div 
                                  key={page.id} 
                                  className={`flex items-center justify-between p-4 rounded-lg border ${
                                    isEnabled ? 'bg-green-50/50 border-green-200 dark:bg-green-900/10 dark:border-green-800' : 'bg-red-50/50 border-red-200 dark:bg-red-900/10 dark:border-red-800'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <PageIcon className={`h-5 w-5 ${isEnabled ? 'text-green-600' : 'text-red-600'}`} />
                                    <div>
                                      <p className="font-medium">{page.name}</p>
                                      <p className="text-xs text-muted-foreground">{page.description}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <Badge variant={isEnabled ? "default" : "secondary"}>
                                      {isEnabled ? "Enabled" : "Disabled"}
                                    </Badge>
                                    <Switch
                                      checked={isEnabled}
                                      onCheckedChange={() => handleToggleCompanyPage(page.id)}
                                      data-testid={`toggle-company-page-${page.id}`}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Enable All Button */}
                    {disabledCompanyPages.length > 0 && (
                      <div className="pt-4 border-t">
                        <Button 
                          variant="outline" 
                          onClick={handleEnableAllCompanyPages}
                          data-testid="enable-all-company-pages"
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Enable All Pages
                        </Button>
                      </div>
                    )}
                    
                    {/* Info Message */}
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm">
                      <p className="text-blue-700 dark:text-blue-300">
                        <strong>Note:</strong> Changes apply globally to all company users. Disabled pages will be hidden from the dashboard navigation but can still be accessed via direct URL.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Individual Pages Visibility */}
              <Card data-testid="admin-individual-pages-visibility">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Individual Pages Visibility
                  </CardTitle>
                  <CardDescription>
                    Enable or disable specific pages for all individual (personal) users. Disabled pages will not appear on any individual user's dashboard.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {INDIVIDUAL_PAGES.map(page => {
                      const PageIcon = page.icon;
                      const isEnabled = !disabledIndividualPages.includes(page.id);
                      
                      return (
                        <div 
                          key={page.id} 
                          className={`flex items-center justify-between p-4 rounded-lg border ${
                            isEnabled ? 'bg-green-50/50 border-green-200 dark:bg-green-900/10 dark:border-green-800' : 'bg-red-50/50 border-red-200 dark:bg-red-900/10 dark:border-red-800'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <PageIcon className={`h-5 w-5 ${isEnabled ? 'text-green-600' : 'text-red-600'}`} />
                            <div>
                              <p className="font-medium">{page.name}</p>
                              <p className="text-xs text-muted-foreground">{page.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge variant={isEnabled ? "default" : "secondary"}>
                              {isEnabled ? "Enabled" : "Disabled"}
                            </Badge>
                            <Switch
                              checked={isEnabled}
                              onCheckedChange={() => handleToggleIndividualPage(page.id)}
                              data-testid={`toggle-individual-page-${page.id}`}
                            />
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Enable All Button */}
                    {disabledIndividualPages.length > 0 && (
                      <div className="pt-4 border-t">
                        <Button 
                          variant="outline" 
                          onClick={handleEnableAllIndividualPages}
                          data-testid="enable-all-individual-pages"
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Enable All Pages
                        </Button>
                      </div>
                    )}
                    
                    {/* Info Message */}
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm">
                      <p className="text-blue-700 dark:text-blue-300">
                        <strong>Note:</strong> Changes apply globally to all individual (personal) users. Disabled pages will be hidden from their dashboard but can still be accessed via direct URL.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Feedback & Coaching Tab */}
          <TabsContent value="feedback">
            <div className="space-y-6">
              {/* AI Simulation Mode */}
              <Card data-testid="ai-simulation-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PlayCircle className="h-5 w-5" />
                    AI Simulation Mode
                  </CardTitle>
                  <CardDescription>Test AI responses using real user data without affecting their account</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Select User to Simulate</Label>
                      <Select value={selectedSimUser} onValueChange={setSelectedSimUser}>
                        <SelectTrigger data-testid="sim-user-select">
                          <SelectValue placeholder="Choose a user..." />
                        </SelectTrigger>
                        <SelectContent>
                          {simulationUsers.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.email}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2 space-y-2">
                      <Label>Test Query</Label>
                      <div className="flex gap-2">
                        <Input
                          value={simulationQuery}
                          onChange={(e) => setSimulationQuery(e.target.value)}
                          placeholder="e.g., What is my expense this month?"
                          onKeyDown={(e) => e.key === "Enter" && handleRunSimulation()}
                          data-testid="sim-query-input"
                        />
                        <Button onClick={handleRunSimulation} disabled={isSimulating || !selectedSimUser} data-testid="run-simulation-btn">
                          {isSimulating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {simulationResponse && (
                    <div className="mt-4 p-4 bg-muted/30 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline">Simulating: {simulationResponse.simulated_user_email}</Badge>
                        <span className="text-xs text-muted-foreground">{simulationResponse.total_tokens} tokens used</span>
                      </div>
                      <div className="p-3 bg-background rounded border">
                        <p className="text-sm whitespace-pre-wrap">{simulationResponse.response}</p>
                      </div>
                      {simulationResponse.thinking_steps?.length > 0 && (
                        <Accordion type="single" collapsible>
                          <AccordionItem value="thinking">
                            <AccordionTrigger className="text-sm">View Thinking Steps</AccordionTrigger>
                            <AccordionContent>
                              <ul className="text-xs space-y-1 text-muted-foreground">
                                {simulationResponse.thinking_steps.map((step, i) => (
                                  <li key={i}>{step}</li>
                                ))}
                              </ul>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* User Feedback */}
              <Card data-testid="ai-feedback-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <MessageSquare className="h-5 w-5" />
                        User Feedback
                      </CardTitle>
                      <CardDescription>Review user feedback on AI responses</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={feedbackFilter} onValueChange={(v) => { setFeedbackFilter(v); }}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="positive">Positive</SelectItem>
                          <SelectItem value="negative">Negative</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="outline" size="sm" onClick={fetchFeedback}>
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {isLoadingFeedback ? (
                    <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>
                  ) : feedbackList.length === 0 ? (
                    <div className="text-center py-8">
                      <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                      <p className="text-muted-foreground">No feedback yet. Users can rate AI responses with thumbs up/down.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {feedbackList.map(fb => (
                        <div key={fb.id} className="p-4 border rounded-lg space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {fb.rating === "positive" ? (
                                <ThumbsUp className="h-5 w-5 text-green-500" />
                              ) : (
                                <ThumbsDown className="h-5 w-5 text-red-500" />
                              )}
                              <span className="text-sm font-medium">{fb.user_email}</span>
                              <span className="text-xs text-muted-foreground">{format(parseISO(fb.created_at), "MMM d, yyyy h:mm a")}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {fb.is_coaching && <Badge variant="outline" className="text-green-600">Coaching</Badge>}
                              {fb.rating === "positive" && !fb.is_coaching && (
                                <Button variant="outline" size="sm" onClick={() => handlePromoteToCoaching(fb.id)}>
                                  Promote to Coaching
                                </Button>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="p-2 bg-muted/30 rounded">
                              <p className="text-xs text-muted-foreground mb-1">Query:</p>
                              <p className="line-clamp-2">{fb.query_text}</p>
                            </div>
                            <div className="p-2 bg-muted/30 rounded">
                              <p className="text-xs text-muted-foreground mb-1">Response:</p>
                              <p className="line-clamp-2">{fb.response_text}</p>
                            </div>
                          </div>
                          {fb.comment && (
                            <p className="text-sm text-muted-foreground italic">"{fb.comment}"</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Coaching Notes */}
              <Card data-testid="ai-coaching-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Brain className="h-5 w-5" />
                        Coaching Notes
                      </CardTitle>
                      <CardDescription>Examples and guidance for AI responses. These are injected into the AI context.</CardDescription>
                    </div>
                    <Button onClick={() => setShowCoachingDialog(true)} data-testid="add-coaching-btn">
                      <Plus className="h-4 w-4 mr-2" /> Add Note
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {coachingNotes.length === 0 ? (
                    <div className="text-center py-8">
                      <Brain className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                      <p className="text-muted-foreground">No coaching notes yet. Add examples to guide AI responses.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {coachingNotes.map(note => (
                        <div key={note.id} className="p-4 border rounded-lg space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{note.category}</Badge>
                              {note.source_feedback_id && <Badge variant="secondary">From Feedback</Badge>}
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteCoachingNote(note.id)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="p-2 bg-muted/30 rounded">
                              <p className="text-xs text-muted-foreground mb-1">Example Query:</p>
                              <p>{note.example_query}</p>
                            </div>
                            <div className="p-2 bg-muted/30 rounded">
                              <p className="text-xs text-muted-foreground mb-1">Good Response:</p>
                              <p className="line-clamp-3">{note.example_response}</p>
                            </div>
                          </div>
                          <div className="p-2 bg-primary/5 rounded">
                            <p className="text-xs text-muted-foreground mb-1">Coaching Note:</p>
                            <p className="text-sm">{note.coaching_note}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Add Coaching Note Dialog */}
      <Dialog open={showCoachingDialog} onOpenChange={setShowCoachingDialog}>
        <DialogContent className="max-w-lg" data-testid="add-coaching-dialog">
          <DialogHeader>
            <DialogTitle>Add Coaching Note</DialogTitle>
            <DialogDescription>Add an example query/response pair to guide AI behavior</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={newCoachingNote.category} onValueChange={(v) => setNewCoachingNote(prev => ({ ...prev, category: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="expenses">Expenses</SelectItem>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="goals">Goals</SelectItem>
                  <SelectItem value="budgeting">Budgeting</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Example Query *</Label>
              <Input
                value={newCoachingNote.example_query}
                onChange={(e) => setNewCoachingNote(prev => ({ ...prev, example_query: e.target.value }))}
                placeholder="e.g., What are my expenses this month?"
              />
            </div>
            <div className="space-y-2">
              <Label>Good Response Example *</Label>
              <Textarea
                value={newCoachingNote.example_response}
                onChange={(e) => setNewCoachingNote(prev => ({ ...prev, example_response: e.target.value }))}
                placeholder="The ideal response the AI should give..."
                className="min-h-[100px]"
              />
            </div>
            <div className="space-y-2">
              <Label>Coaching Note *</Label>
              <Textarea
                value={newCoachingNote.coaching_note}
                onChange={(e) => setNewCoachingNote(prev => ({ ...prev, coaching_note: e.target.value }))}
                placeholder="Instructions for the AI about this type of query..."
                className="min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCoachingDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateCoachingNote}>Create Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Document Dialog */}
      <Dialog open={showAddDocDialog} onOpenChange={setShowAddDocDialog}>
        <DialogContent className="max-w-lg" data-testid="add-doc-dialog">
          <DialogHeader>
            <DialogTitle>Add Text Document</DialogTitle>
            <DialogDescription>Add a text document to the knowledge base</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                placeholder="Document title..."
                value={newDocForm.title}
                onChange={(e) => setNewDocForm(prev => ({ ...prev, title: e.target.value }))}
                data-testid="new-doc-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                placeholder="Enter document content..."
                rows={8}
                value={newDocForm.content_text}
                onChange={(e) => setNewDocForm(prev => ({ ...prev, content_text: e.target.value }))}
                data-testid="new-doc-content-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Tags (comma-separated)</Label>
              <Input
                placeholder="tag1, tag2, tag3..."
                value={newDocForm.tags}
                onChange={(e) => setNewDocForm(prev => ({ ...prev, tags: e.target.value }))}
                data-testid="new-doc-tags-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDocDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTextDocument} disabled={isUploadingDoc} data-testid="create-doc-button">
              {isUploadingDoc ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Translation Editor Dialog */}
      <Dialog open={!!selectedLangForEdit} onOpenChange={(open) => { if (!open) { setSelectedLangForEdit(null); setEditedTranslations({}); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="translation-editor-dialog">
          <DialogHeader>
            <DialogTitle>Translation Editor - {selectedLangForEdit?.native_name} ({selectedLangForEdit?.code})</DialogTitle>
            <DialogDescription>Edit translations for this language. Changes are saved when you click "Save Changes".</DialogDescription>
          </DialogHeader>
          
          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search translations..." 
                  value={translationSearch}
                  onChange={(e) => setTranslationSearch(e.target.value)}
                  className="pl-9"
                  data-testid="translation-search-input"
                />
              </div>
            </div>
            <Select value={selectedNamespace} onValueChange={setSelectedNamespace}>
              <SelectTrigger className="w-[180px]" data-testid="translation-namespace-select">
                <SelectValue placeholder="All namespaces" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All namespaces</SelectItem>
                <SelectItem value="common">common</SelectItem>
                <SelectItem value="landing">landing</SelectItem>
                <SelectItem value="auth">auth</SelectItem>
                <SelectItem value="dashboard">dashboard</SelectItem>
                <SelectItem value="goals">goals</SelectItem>
                <SelectItem value="settings">settings</SelectItem>
                <SelectItem value="admin">admin</SelectItem>
                <SelectItem value="errors">errors</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1 overflow-auto border rounded-lg">
            {isLoadingTranslations ? (
              <div className="p-8 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                <p className="text-muted-foreground">Loading translations...</p>
              </div>
            ) : (
              <Table>
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow>
                    <TableHead className="w-[100px]">Namespace</TableHead>
                    <TableHead className="w-[150px]">Key</TableHead>
                    <TableHead className="w-[200px]">English (Default)</TableHead>
                    <TableHead>Translation</TableHead>
                    <TableHead className="w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getFilteredTranslations().map(t => (
                    <TableRow key={t.id} className={editedTranslations[t.id] !== undefined ? "bg-[hsl(var(--ff-warning)/.05)]" : ""}>
                      <TableCell className="font-mono text-xs">{t.namespace}</TableCell>
                      <TableCell className="font-mono text-xs">{t.key}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{t.default_value}</TableCell>
                      <TableCell>
                        <Input
                          value={editedTranslations[t.id] !== undefined ? editedTranslations[t.id] : t.value}
                          onChange={(e) => setEditedTranslations({...editedTranslations, [t.id]: e.target.value})}
                          className={`text-sm ${t.is_custom ? "border-[hsl(var(--ff-info))]" : ""}`}
                          data-testid={`translation-input-${t.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        {t.is_custom && (
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleResetTranslation(t.id)}
                            title="Reset to default"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          <DialogFooter className="mt-4">
            <div className="flex items-center gap-2 mr-auto text-sm text-muted-foreground">
              {Object.keys(editedTranslations).length > 0 && (
                <span>{Object.keys(editedTranslations).length} unsaved changes</span>
              )}
            </div>
            <Button variant="outline" onClick={() => { setSelectedLangForEdit(null); setEditedTranslations({}); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveTranslations} 
              disabled={isSavingTranslations || Object.keys(editedTranslations).length === 0}
              data-testid="save-translations-button"
            >
              {isSavingTranslations ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Package Dialog */}
      <Dialog open={showPackageDialog} onOpenChange={setShowPackageDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="package-dialog">
          <DialogHeader>
            <DialogTitle>{editingPackage ? "Edit Package" : "Create Package"}</DialogTitle>
            <DialogDescription>Configure the package details, limits, and AI features</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSavePackage} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div className="space-y-2">
              <Label>Package Name</Label>
              <Input value={packageForm.name} onChange={e => setPackageForm({...packageForm, name: e.target.value})} placeholder="e.g., Pro" data-testid="package-name-input" required />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={packageForm.description} onChange={e => setPackageForm({...packageForm, description: e.target.value})} placeholder="e.g., For power users" data-testid="package-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Monthly Limit</Label>
                <Input type="number" min="1" value={packageForm.monthly_limit} onChange={e => setPackageForm({...packageForm, monthly_limit: e.target.value})} placeholder="50" data-testid="package-limit-input" required />
              </div>
              <div className="space-y-2">
                <Label>Sort Order</Label>
                <Input type="number" value={packageForm.sort_order} onChange={e => setPackageForm({...packageForm, sort_order: e.target.value})} placeholder="1" data-testid="package-sort-input" />
              </div>
            </div>
            
            {/* AI Features Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                <Bot className="h-4 w-4" />
                AI Agent Features
              </h4>
              
              <div className="flex items-center justify-between p-3 border rounded-lg mb-4">
                <div>
                  <Label className="font-medium">Enable AI Chat</Label>
                  <p className="text-xs text-muted-foreground">Allow users with this package to use AI chat</p>
                </div>
                <Switch
                  checked={packageForm.ai_enabled}
                  onCheckedChange={(checked) => setPackageForm({...packageForm, ai_enabled: checked})}
                  data-testid="package-ai-enabled-switch"
                />
              </div>
              
              {packageForm.ai_enabled && (
                <div className="space-y-4 pl-2 border-l-2 border-primary/20">
                  <div className="space-y-2">
                    <Label className="text-sm">OpenRouter Token Limit (per month)</Label>
                    <div className="flex items-center gap-2">
                      <Input 
                        type="number" 
                        min="0" 
                        value={packageForm.ai_token_limit} 
                        onChange={e => setPackageForm({...packageForm, ai_token_limit: e.target.value})} 
                        placeholder="0 = unlimited" 
                        data-testid="package-ai-token-input" 
                      />
                      <span className="text-xs text-muted-foreground whitespace-nowrap">0 = unlimited</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm">Tavily Search Limit (per month)</Label>
                    <div className="flex items-center gap-2">
                      <Input 
                        type="number" 
                        min="0" 
                        value={packageForm.tavily_token_limit} 
                        onChange={e => setPackageForm({...packageForm, tavily_token_limit: e.target.value})} 
                        placeholder="0 = unlimited" 
                        data-testid="package-tavily-token-input" 
                      />
                      <span className="text-xs text-muted-foreground whitespace-nowrap">0 = unlimited</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm">Perplexity Search Limit (per month)</Label>
                    <div className="flex items-center gap-2">
                      <Input 
                        type="number" 
                        min="0" 
                        value={packageForm.perplexity_token_limit} 
                        onChange={e => setPackageForm({...packageForm, perplexity_token_limit: e.target.value})} 
                        placeholder="0 = unlimited" 
                        data-testid="package-perplexity-token-input" 
                      />
                      <span className="text-xs text-muted-foreground whitespace-nowrap">0 = unlimited</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPackageDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={isSavingPackage} data-testid="package-submit-button">
                {isSavingPackage ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{editingPackage ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* User Overview Dialog */}
      <Dialog open={!!selectedUserId} onOpenChange={open => { if (!open) { setSelectedUserId(null); setUserOverview(null); } }}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="user-overview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedUser?.user_type === "company" ? <Building className="h-5 w-5" /> : <Users className="h-5 w-5" />}
              User Details: {selectedUser?.email}
            </DialogTitle>
            <DialogDescription>
              {selectedUser?.user_type === "company" ? "Business account details and financial overview" : "Personal account details and financial overview"}
            </DialogDescription>
          </DialogHeader>
          {isLoadingOverview ? (
            <div className="space-y-3"><Skeleton className="h-20 w-full" /><Skeleton className="h-40 w-full" /></div>
          ) : selectedUser ? (
            <div className="space-y-6">
              {/* User Info Section */}
              <div className="p-4 rounded-lg bg-muted/30 border">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Account Information
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Email</p>
                    <p className="font-medium">{selectedUser.email}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Account Type</p>
                    <Badge variant="outline" className={selectedUser.user_type === "company" ? "bg-blue-500/10 text-blue-600 border-blue-500/30" : "bg-gray-500/10 text-gray-600 border-gray-500/30"}>
                      {selectedUser.user_type === "company" ? "Business" : "Personal"}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Package</p>
                    <p className="font-medium">{selectedUser.package_name || "No Package"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Status</p>
                    <Badge variant="outline" className={selectedUser.is_active ? "status-paid" : "status-pending"}>
                      {selectedUser.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Joined</p>
                    <p className="font-medium">{format(parseISO(selectedUser.created_at), "MMM d, yyyy")}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Monthly Usage</p>
                    <p className="font-medium">
                      {selectedUser.monthly_limit === 0 ? (
                        `${selectedUser.monthly_usage} transactions (Unlimited)`
                      ) : selectedUser.monthly_limit ? (
                        `${selectedUser.monthly_usage}/${selectedUser.monthly_limit} transactions`
                      ) : "-"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Company Details Section (only for business accounts) */}
              {selectedUser.user_type === "company" && (
                <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                  <h3 className="font-semibold mb-3 flex items-center gap-2 text-blue-600">
                    <Building className="h-4 w-4" />
                    Business Details
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Company Name</p>
                      <p className="font-medium">{selectedUser.company_name || "-"}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Industry</p>
                      <p className="font-medium capitalize">{selectedUser.company_industry?.replace("_", " & ") || "-"}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Company Size</p>
                      <p className="font-medium capitalize">
                        {selectedUser.company_size === "solo" ? "Solo / Freelancer" :
                         selectedUser.company_size === "small" ? "Small (2-10 employees)" :
                         selectedUser.company_size === "medium" ? "Medium (11-50 employees)" :
                         selectedUser.company_size === "large" ? "Large (50+ employees)" : "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Fiscal Year Start</p>
                      <p className="font-medium">
                        {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"][selectedUser.fiscal_year_start - 1] || "January"}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* AI Usage Section */}
              <div className="p-4 rounded-lg bg-purple-500/5 border border-purple-500/20">
                <h3 className="font-semibold mb-3 flex items-center gap-2 text-purple-600">
                  <Bot className="h-4 w-4" />
                  AI Usage Statistics
                </h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Total Queries</p>
                    <p className="font-medium text-lg">{(selectedUser.total_queries || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Monthly Tokens</p>
                    <p className="font-medium text-lg">{(selectedUser.month_tokens_used || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Total Tokens</p>
                    <p className="font-medium text-lg">{(selectedUser.total_tokens_used || 0).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Financial Overview Section */}
              {userOverview && (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Income</p><p className="text-xl font-bold font-numeric income-text">${userOverview.total_income.toFixed(2)}</p></CardContent></Card>
                    <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Expenses</p><p className="text-xl font-bold font-numeric expense-text">${userOverview.total_expenses.toFixed(2)}</p></CardContent></Card>
                    <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Balance</p><p className={"text-xl font-bold font-numeric " + (userOverview.balance >= 0 ? "income-text" : "expense-text")}>${userOverview.balance.toFixed(2)}</p></CardContent></Card>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    <h3 className="font-semibold mb-2">Recent Transactions</h3>
                    {userOverview.transactions.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No transactions this month</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {userOverview.transactions.map(tx => (
                            <TableRow key={tx.id}>
                              <TableCell>{format(parseISO(tx.date), "MMM d")}</TableCell>
                              <TableCell>{tx.category_name || "-"}</TableCell>
                              <TableCell><Badge className={tx.type === "income" ? "income-bg income-text border-0" : "expense-bg expense-text border-0"}>{tx.type}</Badge></TableCell>
                              <TableCell className={"text-right font-numeric " + (tx.type === "income" ? "income-text" : "expense-text")}>${tx.amount.toFixed(2)}</TableCell>
                              <TableCell><Badge variant="outline" className={tx.paid ? "status-paid" : "status-pending"}>{tx.paid ? "Paid" : "Pending"}</Badge></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Query Detail Dialog */}
      <Dialog open={showQueryDetailDialog} onOpenChange={setShowQueryDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto" data-testid="query-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Query Detail
            </DialogTitle>
            <DialogDescription>
              Full thinking trace and audit log
            </DialogDescription>
          </DialogHeader>
          {selectedQueryDetail && (
            <div className="space-y-4">
              {/* Query Info */}
              <div className="p-4 rounded-lg bg-muted/30 border">
                <Label className="text-xs text-muted-foreground">User Query</Label>
                <p className="font-medium mt-1">{selectedQueryDetail.query}</p>
              </div>

              {/* Response */}
              <div className="p-4 rounded-lg bg-[hsl(var(--ff-primary)/.05)] border border-[hsl(var(--ff-primary)/.2)]">
                <Label className="text-xs text-muted-foreground">AI Response</Label>
                <p className="mt-1 whitespace-pre-wrap text-sm">{selectedQueryDetail.response}</p>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-4 gap-3">
                <div className="p-3 rounded border text-center">
                  <p className="text-xs text-muted-foreground">Tokens Used</p>
                  <p className="font-bold">{selectedQueryDetail.total_tokens_used || 0}</p>
                </div>
                <div className="p-3 rounded border text-center">
                  <p className="text-xs text-muted-foreground">Response Time</p>
                  <p className="font-bold">{selectedQueryDetail.response_time_ms || 0}ms</p>
                </div>
                <div className="p-3 rounded border text-center">
                  <p className="text-xs text-muted-foreground">Model</p>
                  <p className="font-bold text-xs">{selectedQueryDetail.model_used?.split('/')[1] || '-'}</p>
                </div>
                <div className="p-3 rounded border text-center">
                  <p className="text-xs text-muted-foreground">Fallback Used</p>
                  <p className="font-bold">{selectedQueryDetail.fallback_used ? 'Yes' : 'No'}</p>
                </div>
              </div>

              {/* Thinking Trace */}
              {selectedQueryDetail.thinking_trace && (
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="phase1">
                    <AccordionTrigger className="text-sm">
                      Phase 1: Intent Classification
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Classification:</span>
                          <Badge variant="outline">{selectedQueryDetail.thinking_trace.phase_1_intent?.classification || '-'}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Guardrail Check:</span>
                          <Badge variant={selectedQueryDetail.thinking_trace.phase_1_intent?.guardrail_check === 'PASSED' ? 'default' : 'destructive'}>
                            {selectedQueryDetail.thinking_trace.phase_1_intent?.guardrail_check || '-'}
                          </Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Tokens:</span>
                          <span>{selectedQueryDetail.thinking_trace.phase_1_intent?.tokens_used || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Duration:</span>
                          <span>{selectedQueryDetail.thinking_trace.phase_1_intent?.duration_ms || 0}ms</span>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="phase2">
                    <AccordionTrigger className="text-sm">
                      Phase 2: Data Gathering
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Chunks Retrieved:</span>
                          <span>{selectedQueryDetail.thinking_trace.phase_2_data_gathering?.total_chunks_retrieved || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Duration:</span>
                          <span>{selectedQueryDetail.thinking_trace.phase_2_data_gathering?.duration_ms || 0}ms</span>
                        </div>
                        {selectedQueryDetail.thinking_trace.phase_2_data_gathering?.sources_queried?.length > 0 && (
                          <div>
                            <span className="text-muted-foreground">Sources Queried:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {selectedQueryDetail.thinking_trace.phase_2_data_gathering.sources_queried.map((s, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{s.type}: {s.count}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="phase3">
                    <AccordionTrigger className="text-sm">
                      Phase 3: Source Ranking
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Ranking Model:</span>
                          <span className="text-xs">{selectedQueryDetail.thinking_trace.phase_3_ranking?.ranking_model?.split('/')[1] || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Sources Selected:</span>
                          <span>{selectedQueryDetail.thinking_trace.phase_3_ranking?.sources_selected?.length || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Tokens:</span>
                          <span>{selectedQueryDetail.thinking_trace.phase_3_ranking?.tokens_used || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Duration:</span>
                          <span>{selectedQueryDetail.thinking_trace.phase_3_ranking?.duration_ms || 0}ms</span>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="phase4">
                    <AccordionTrigger className="text-sm">
                      Phase 4: Response Generation
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Model:</span>
                          <span className="text-xs">{selectedQueryDetail.thinking_trace.phase_4_generation?.model_used?.split('/')[1] || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Context Tokens:</span>
                          <span>{selectedQueryDetail.thinking_trace.phase_4_generation?.context_tokens || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Response Tokens:</span>
                          <span>{selectedQueryDetail.thinking_trace.phase_4_generation?.response_tokens || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Duration:</span>
                          <span>{selectedQueryDetail.thinking_trace.phase_4_generation?.duration_ms || 0}ms</span>
                        </div>
                        {selectedQueryDetail.thinking_trace.phase_4_generation?.guardrails_applied?.length > 0 && (
                          <div>
                            <span className="text-muted-foreground">Guardrails Applied:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {selectedQueryDetail.thinking_trace.phase_4_generation.guardrails_applied.map((g, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">{g}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              )}

              {/* Guardrails Triggered */}
              {selectedQueryDetail.guardrails_triggered?.length > 0 && (
                <div className="p-3 rounded-lg border border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
                  <Label className="text-xs text-amber-600">Guardrails Triggered</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedQueryDetail.guardrails_triggered.map((g, i) => (
                      <Badge key={i} className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">{g}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQueryDetailDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
